import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ClicksInfoButton, CPDCInfoButton } from "@/components/ui/MetricInfo";
import OfferManager from "@/components/offers/OfferManager";
import { toast } from "sonner";
import { 
  Megaphone, Plus, Archive, Trash2, Edit, Play, Pause, Loader2, Eye, DollarSign, 
  MoreHorizontal, MapPin, Target, Clock, Image, Upload, X, Save, Pencil, AlertTriangle,
  Library, Check, ImagePlus, Gift, Ticket, Copy, Search, Building2, Map, Tag, Sparkles
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, ChevronUp, Calendar, CreditCard, Info } from "lucide-react";
import { CampaignPaymentModal } from "@/components/CampaignPaymentModal";
import { usePermissions } from "@/lib/permissions";
import {
  NONE_SELECTED,
  summariseAges, summariseDays, summariseGender, summariseInterests,
  summariseLocations, summariseRadius, summariseTimes, summariseVenueTypes,
} from "@/lib/audienceSummary";

const surfaces = [
  { value: "PASSENGER_APP", label: "Passenger App" },
  { value: "DRIVER_APP", label: "Driver App" },
  { value: "PANEL", label: "Drive Panel" },
];

const statusColors = {
  DRAFT: "status-draft",
  ACTIVE: "status-active",
  PAUSED: "status-paused",
  COMPLETED: "status-completed",
};

const statusLabels = {
};

const daysOfWeek = [
  { value: "monday", label: "Mon" },
  { value: "tuesday", label: "Tue" },
  { value: "wednesday", label: "Wed" },
  { value: "thursday", label: "Thu" },
  { value: "friday", label: "Fri" },
  { value: "saturday", label: "Sat" },
  { value: "sunday", label: "Sun" },
];

const timeSlots = [
  { value: "morning", label: "Morning (6am-12pm)" },
  { value: "afternoon", label: "Afternoon (12pm-6pm)" },
  { value: "evening", label: "Evening (6pm-10pm)" },
  { value: "night", label: "Night (10pm-6am)" },
];

const ageRanges = [
  { value: "18-24", label: "18-24" },
  { value: "25-34", label: "25-34" },
  { value: "35-44", label: "35-44" },
  { value: "45-54", label: "45-54" },
  { value: "55+", label: "55+" },
];

const venueTypes = [
  { value: "restaurant", label: "Restaurant" },
  { value: "retail", label: "Retail" },
  { value: "entertainment", label: "Entertainment" },
  { value: "services", label: "Services" },
  { value: "nightlife", label: "Nightlife" },
  { value: "airport", label: "Airport" },
  { value: "sporting_facility", label: "Sporting Facility" },
  { value: "university", label: "University" },
  { value: "hospital", label: "Hospital" },
  { value: "shopping_centre", label: "Shopping Centre" },
];

// Round 4, G-5 asked for Pets, Outdoors and Music plus "anything else that should be
// there for MVP". Events is included because it is the one a merchant near a stadium or
// theatre would look for first and its absence would be conspicuous.
//
// Values are the stored keys and must not be renamed: a campaign saved with "food" keeps
// matching only if the key survives. Labels are free to change.
const interestOptions = [
  { value: "food", label: "Food & Dining" },
  { value: "shopping", label: "Shopping" },
  { value: "travel", label: "Travel" },
  { value: "fitness", label: "Fitness" },
  { value: "technology", label: "Technology" },
  { value: "entertainment", label: "Entertainment" },
  { value: "pets", label: "Pets" },
  { value: "outdoors", label: "Outdoors" },
  { value: "music", label: "Music" },
  { value: "events", label: "Events" },
];

// Draft storage key
const CAMPAIGN_DRAFT_KEY = 'booma_campaign_draft';

// Fallback tier config used while loading from API. Prices mirror backend
// CAMPAIGN_TIER_CONFIG in models/enums.py. UI derives per-duration price as
// price_monthly × duration_months, so no nested pricing dict is needed.
// The four durations and how they read. One declaration, used by the campaign form's
// selector and its end-date note (round 11, SP-38): the selector used to hardcode its
// four options, so an admin unticking one in Tier Pricing changed the stored config and
// the form kept offering it.
// A local calendar date, YYYY-MM-DD (round 11).
//
// `new Date().toISOString()` converts to UTC first. Australia is UTC+10 or +11, so for
// the first ten hours of every local day it returns YESTERDAY - which is how Robin
// could pick a start date of 10 September at 9:26am on the 11th. `en-CA` is the
// shortest way to get an ISO-shaped date in the LOCAL zone without a date library.
function toLocalDateString(date) {
  return date.toLocaleDateString("en-CA");
}

const ALL_DURATIONS = ["1_month", "3_months", "6_months", "12_months"];
const DURATION_LABELS = {
  "1_month": "1 Month",
  "3_months": "3 Months",
  "6_months": "6 Months",
  "12_months": "12 Months",
};

const DEFAULT_TIER_CONFIG = {
  BRONZE: { name: "Bronze", price_monthly: 50, max_campaigns_per_account: 1, max_offers: 0, max_creatives: 1, allowed_durations: ["12_months"], display_priority: 1, enabled: true, features: { cpm_enabled: false, cpc_enabled: false, cpdc_enabled: false, deals_enabled: false, basic_targeting: false, advanced_targeting: false, animated_creatives: false, pause_enabled: false, featured_on_map: false, featured_in_discovery: false }, description: "Views only - great for brand awareness", tagline: "Standard Impression Model", is_free: false },
  SILVER: { name: "Silver", price_monthly: 199, max_campaigns_per_account: 3, max_offers: 3, max_creatives: 3, allowed_durations: ["1_month", "3_months", "6_months", "12_months"], display_priority: 2, enabled: false, features: { cpm_enabled: true, cpc_enabled: true, cpdc_enabled: true, deals_enabled: true, basic_targeting: true, advanced_targeting: false, animated_creatives: false, pause_enabled: false, featured_on_map: false, featured_in_discovery: true }, description: "Views + Engagement with delivered customers", tagline: "Pay for Engagement", is_free: false },
  GOLD: { name: "Gold", price_monthly: 499, max_campaigns_per_account: 5, max_offers: 5, max_creatives: 5, allowed_durations: ["1_month", "3_months", "6_months", "12_months"], display_priority: 3, enabled: false, features: { cpm_enabled: true, cpc_enabled: true, cpdc_enabled: true, deals_enabled: true, basic_targeting: true, advanced_targeting: true, animated_creatives: true, pause_enabled: true, featured_on_map: true, featured_in_discovery: true }, description: "Intent audiences with AI and full flexibility", tagline: "Intent Audiences", is_free: false },
};

// Discovery Boost, round 4 G-7: 1 or 2 per campaign, ten Feature Offers each. Mirrors
// BOOST_MAX_PER_CAMPAIGN and BOOST_OFFERS_PER_ADDON in campaign_rules.py, which are the
// authority - the server validates the quantity and prices it, so a drift here shows as
// a refused purchase rather than a wrong charge.
const BOOST_MAX_PER_CAMPAIGN = 2;
const BOOST_OFFERS_PER_BOOST = 10;

// Bronze campaigns have a fixed ad-spend budget cap (not user-editable)
const BRONZE_BUDGET_CAP = 500;

// Default campaign state
// spec 7.2: the main heading comes from a pre-populated menu or is entered manually.
// A convenience, not a constraint - the field stays free text.
const CAMPAIGN_NAME_PRESETS = ["Happy Hour", "Everyday", "Weekend", "Sale", "Lunch Time"];

// spec 7.4. RESERVATIONS is deliberately absent - spec 7.4 marks it "(future)" and
// spec 18 says it is not required for MVP, so offering it would sell something that does
// not exist. `needsOffers` mirrors OFFER_DEPENDENT_OBJECTIVES on the backend: these
// cannot be pursued by a tier with no clickable offer.
// Round 2 parked the objective pending a workshop on what it should mean. The select is
// built and tested; this hides it rather than deleting it. See the block in the campaign
// form for what has to be decided before it returns.
const SHOW_CAMPAIGN_OBJECTIVE = false;

const CAMPAIGN_OBJECTIVES = [
  { value: "AWARENESS", label: "Awareness", help: "Get the business seen on-route.", needsOffers: false },
  { value: "VENUE_VISITS", label: "Venue Visits", help: "Drive customers to the venue.", needsOffers: true },
  { value: "ADD_STOPS", label: "Add Stops", help: "Get added as a stop mid-trip.", needsOffers: true },
  { value: "SAVES", label: "Saves", help: "Have offers saved for later.", needsOffers: true },
  { value: "REDEEMS", label: "Redeems", help: "Get offers redeemed in venue.", needsOffers: true },
  { value: "REVENUE", label: "Revenue", help: "Maximise revenue per customer.", needsOffers: true },
];

const getDefaultCampaign = () => ({
  name: "",
  description: "",
  objective: "",  // spec 7.4 - the campaign subheading; validated against the tier
  tier: "",  // Campaign-level tier - not pre-selected; user must actively choose (#15)
  surfaces: ["PASSENGER_APP", "PANEL"],
  start_at: "",
  end_at: "",
  duration: "",  // Fixed duration: 1_month, 3_months, 6_months, 12_months
  budget_limit: "",
  pricing: {
    cpm_enabled: true,
    cpc_enabled: false,
    cpdc_enabled: false,  // Single toggle for all Drop Off types
    cpdc_direct_enabled: false,
    cpdc_proximity_enabled: false,
    cpdc_prompted_enabled: false
  },
  creative_ids: [],
  // Up to max_creatives Content (round 4, G-20: Bronze 1, Silver 3, Gold 5). Each
  // one's per-placement renditions are flattened into creative_ids, which is what the
  // campaign has always stored, so the wire format is unchanged.
  selectedContents: [],
  // Deal configuration
  deal_available: false,
  deal_description: "",
  offer_text: "",
  cta_text: "Claim Deal",
  // Sponsored Trips add-on (#18) — quantity only; price/total come from the global rate
  // Both add-ons are OFF by default (round 2). They were an always-visible quantity
  // box and a duration-priced toggle, which read as things you had to configure
  // rather than things you could decline.
  sponsored_trips_enabled: false,
  sponsored_trips_qty: 0,
  discovery_boost_enabled: false,
  // 1 or 2 (round 4, G-7): 10 or 20 Feature Offers, $200 or $400. The server validates
  // it and prices it; this is only what the merchant asked for.
  discovery_boost_qty: 1,
  discovery_boost_image: "business",  // business logo, or this campaign's content
  targeting: {
    location_ids: [],
    states: [],
    postcodes: [],
    suburbs: [],
    locality_pairs: {},
    radius_override: null,
    days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
    time_slots: ["morning", "afternoon", "evening", "night"],
    gender: "all",
    age_ranges: ["all"],  // Multi-select array
    venue_types: ["any"],  // Multi-select array
    interests: ["any"],  // Multi-select array
    enabled: true
  }
});

// The selected localities as one chip each (#21).
//
// Pairing is DERIVED, not required. locality_pairs is written when a search result is
// picked, but a campaign saved before this existed has only the two arrays - and it still
// has to render. So: every suburb becomes a chip, carrying its postcode when we know it,
// and any postcode not claimed by a suburb becomes its own chip rather than disappearing.
//
// Zipping the two arrays by index would have been shorter and wrong: they diverge as soon
// as one suburb is added without a postcode, and every later pair would be mislabelled.
function selectedLocalities(targeting) {
  const suburbs = targeting?.suburbs || [];
  const postcodes = targeting?.postcodes || [];
  const pairs = targeting?.locality_pairs || {};

  const claimed = new Set();
  const chips = suburbs.map(suburb => {
    const postcode = pairs[suburb];
    // Only claim a postcode that is actually still selected - a merchant may have
    // removed it from an older draft while the suburb stayed.
    const paired = postcode && postcodes.includes(postcode) ? postcode : null;
    if (paired) claimed.add(paired);
    return {
      key: `s:${suburb}`,
      label: paired ? `${suburb} ${paired}` : suburb,
      suburb,
      postcode: paired,
    };
  });

  const orphans = postcodes
    .filter(p => !claimed.has(p))
    .map(postcode => ({ key: `p:${postcode}`, label: postcode, suburb: null, postcode }));

  return [...chips, ...orphans];
}

// The tier inclusion list, in the order a merchant reads it. Views is always included
// on every tier, so it has no excluded form.
//
// Labels live here and nowhere else. The previous version wrote each one twice inside a
// ternary, ten rows deep, which is precisely why the Delivered Customers rename missed
// this list while the METRIC_LABELS map was updated.
const TIER_FEATURE_ROWS = [
  // Views is on every tier, but only Silver and Gold CHARGE for it (round 7, R1). The
  // row means "you get this", which Bronze does, so it stays included there.
  { key: "cpm_enabled", label: "Views", alwaysIncluded: true },
  { key: "cpc_enabled", label: "Clicks" },
  { key: "cpdc_enabled", label: "Delivered Customers" },
  // SP-15: "in inclusions change offer to Custom Offers".
  { key: "deals_enabled", label: "Custom Offers" },
  // SP-13 removed "Basic Audience" from all three tiers.
  { key: "advanced_targeting", label: "Advanced Audience" },
  // SP-16: "for inclusions include Booma Smart Offers or Ai Optimised Booma Smart
  // Offers (gold is the only one that gets these)". ai_engine is the Gold-only flag
  // Smart Offers are already gated on, so the row is accurate by construction.
  { key: "ai_engine", label: "AI Optimised Booma Smart Offers" },
  { key: "animated_creatives", label: "Animated Creatives" },
  { key: "pause_enabled", label: "Campaign Pause" },
  { key: "featured_on_map", label: "Featured on live map" },
  { key: "featured_in_discovery", label: "Featured in discovery" },
  // SP-12: "bronze inclusions should have something to capture venue can gain
  // followers". True on every tier - favouriting is how a customer follows a venue and
  // it is the ONE thing a Bronze creative can earn, which is why its absence was
  // conspicuous there.
  { key: "__followers", label: "Customers can follow your venue", alwaysIncluded: true },
];

/**
 * The inclusion rows for one tier, in a FIXED order (round 7, SP-14).
 *
 * This used to sort included rows to the top, which was round 2's item #14 and is the
 * "greens jump around" Joel is now objecting to. He is right, and it is a real
 * improvement rather than a swap: three tier columns side by side exist to be compared
 * ROW BY ROW, and sorting each column independently is exactly what stops that working.
 * Views landing on row one for Bronze and row one for Silver was a coincidence.
 */
function partitionTierFeatures(features) {
  return TIER_FEATURE_ROWS.map(r => ({
    ...r,
    included: r.alwaysIncluded ? true : !!(features || {})[r.key],
  }));
}

// Mirrors the rule in routes/campaigns.py: an unpaid draft with nothing billed against
// it is deleted outright, anything else is archived. The server checks the ad ledger too,
// which the client cannot see, so it stays authoritative - this decides only what the
// menu and the confirmation SAY. They must say the same thing as each other, hence one
// function rather than the condition written out at each site.
function deleteRule(campaign) {
  return !!campaign
    && campaign.status === "DRAFT"
    && campaign.payment_status !== "paid"
    && campaign.discovery_boost?.payment_status !== "paid";
}

export default function Campaigns() {
  const { token } = useAuth();
  // Round 10. The API enforces all of this; hiding the control is so a
  // merchant is not offered a button that was never going to work.
  const { can: canDo } = usePermissions();
  const canWriteCampaigns = canDo("campaigns:write");
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [locations, setLocations] = useState([]);
  const [globalPricing, setGlobalPricing] = useState(null);
  const [CAMPAIGN_TIER_CONFIG, setCampaignTierConfig] = useState(DEFAULT_TIER_CONFIG);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);  // #21: set when editing an existing campaign
  // The Add Ons Show/Hide state. Both extras are always visible now (2026-09-03) -
  // each is a switch defaulting to off, which already says "not interested" without
  // a second control in front of it.
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [creativePickerOpen, setCreativePickerOpen] = useState(false);
  const [contentList, setContentList] = useState([]); // Content library for the campaign Content slot (#8/P2-5)
  // Every offer this account owns, grouped per campaign for the detail panel
  // (round 9, TP-15). The panel used to show the legacy deal_* scalars instead.
  const [offers, setOffers] = useState([]);
  
  // Draft state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftRestoredToast, setDraftRestoredToast] = useState(false);
  
  // Tier details expanded state (single toggle for all tiers)
  const [tiersExpanded, setTiersExpanded] = useState(false);
  
  // Archive confirmation modal state
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState(null);
  // Set when the server refuses a budget edit that would halt delivery (7.0.3). Holds
  // the figures it returned, so the dialog quotes the server rather than recomputing.
  const [budgetStop, setBudgetStop] = useState(null);
  const deletesOutright = deleteRule(campaignToArchive);
  const [archiving, setArchiving] = useState(false);

  // TKT-82: Payment modal state (for campaign save prepayment & retry)
  const [paymentModal, setPaymentModal] = useState({
    open: false,
    clientSecret: null,
    amount: 0,
    breakdown: null,   // server-priced line items for the itemised modal (spec 8.1.12)
    campaignName: "",
  });
  
  // Location targeting state
  const [locationTargetingOpen, setLocationTargetingOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  const [australianStates, setAustralianStates] = useState([]);
  
  // AI Deal Suggestion state
  
  // Confirm save dialog
  
  const [newCampaign, setNewCampaign] = useState(getDefaultCampaign());

  // What the campaign IS, as opposed to what the form is proposing. Everything below
  // reads from here rather than from `newCampaign`, because the backend decides against
  // the stored document and the two disagree the moment a merchant touches a control.
  const persistedCampaign = editingCampaignId
    ? campaigns.find((c) => c.id === editingCampaignId)
    : null;

  // Offer limits are enforced by the backend against the persisted tier (routes/offers.py
  // _tier_config_for), so a merchant who switched the picker to Gold without saving was
  // shown "3 of 5" and then refused the fourth offer. Offers hang off the campaign
  // document, so they can only ever be governed by what that document says.
  const persistedTier = persistedCampaign?.tier || "";
  const tierChangePending = !!editingCampaignId
    && !!newCampaign.tier
    && newCampaign.tier !== persistedTier;

  // Already paid for, so the form offers Save rather than Save + Launch.
  const persistedIsLive = (persistedCampaign?.payment_status === "paid")
    || ["ACTIVE", "PAUSED", "COMPLETED", "EXPIRED"].includes(persistedCampaign?.status);
  const [saving, setSaving] = useState(false);

  // Track if draft has been restored in this session to prevent duplicate toasts
  const draftRestoredRef = useRef(false);
  // Track if this is the first render or if we're returning from another page
  const isFirstRenderRef = useRef(true);

  // Check for existing draft - runs when component mounts or when navigating back to this page
  useEffect(() => {
    // On first render, reset the restoration flag to allow checking
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      draftRestoredRef.current = false;
    }
    
    // Already restored in this session
    if (draftRestoredRef.current) return;
    // User already has form open
    if (isCreating) return;
    
    const savedDraft = localStorage.getItem(CAMPAIGN_DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Check if draft has meaningful content
        if (draft.name || draft.description || draft.selectedContentIds?.length ||
            draft.selectedContentId ||
            draft.start_at || draft.end_at || draft.budget_limit) {

          // Mark as restored before setting state to prevent re-runs in this session
          draftRestoredRef.current = true;

          // Restore the Content objects from their ids (empty if content has not loaded
          // yet). selectedContentId, singular, is read as well: a draft saved before
          // G-20 made this a list is still restorable rather than silently losing its
          // image.
          const draftIds = draft.selectedContentIds
            || (draft.selectedContentId ? [draft.selectedContentId] : []);
          const restoredDraft = {
            ...draft,
            selectedContents: draftIds
              .map(id => contentList.find(ct => ct.id === id))
              .filter(Boolean),
          };
          delete restoredDraft.selectedContentId;
          
          setNewCampaign(restoredDraft);
          setHasDraft(true);
          setIsCreating(true);
          
          // Show toast notification after a brief delay
          setTimeout(() => {
            toast.success("Draft campaign restored", {
              description: "Your previous progress has been recovered",
              duration: 4000
            });
          }, 500);
        }
      } catch (e) {
        console.error("Failed to parse campaign draft:", e);
        localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
      }
    }
  }, [creatives, contentList, isCreating]); // Re-run when creatives/content load or isCreating changes
  
  // Reset flags when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      isFirstRenderRef.current = true;
    };
  }, []);

  // Helper to create a lightweight draft for storage (no large image data)
  const createLightweightDraft = (campaign) => {
    return {
      ...campaign,
      // Ids only, never the objects: those carry image data and localStorage has a few
      // megabytes. That mattered with one image and matters more with five (G-20).
      selectedContents: [],
      selectedContentIds: (campaign.selectedContents || []).map(c => c.id),
    };
  };

  // Mirror the form to localStorage while it has never been saved to the server.
  // Crash recovery only, and it stops the moment the campaign has an id - after that the
  // server copy is the draft and a second one in the browser could only disagree with it.
  useEffect(() => {
    if (!isCreating || editingCampaignId) return;

    const timer = setTimeout(() => {
      if (newCampaign.name || newCampaign.description || newCampaign.selectedContents?.length ||
          newCampaign.start_at || newCampaign.end_at ||
          newCampaign.budget_limit) {
        try {
          localStorage.setItem(CAMPAIGN_DRAFT_KEY, JSON.stringify(createLightweightDraft(newCampaign)));
          setHasDraft(true);
        } catch (e) {
          console.error("Failed to save draft:", e);
        }
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [newCampaign, isCreating]);

  // Autosave the draft to the SERVER (round 2).
  //
  // Persisting early is what makes offers reachable during creation - OfferManager needs
  // a campaign_id - and what stops a trip to Creative Builder losing the draft. Both were
  // raised in round 2 as the same complaint from different directions.
  //
  // Deliberately narrow about when it runs:
  //   - only while the form is open, and only once there is a name, because the name is
  //     the one field that makes a draft findable again
  //   - never on a campaign that has been paid for. A live campaign's edits are the
  //     merchant's to confirm, and quietly writing them would change a running campaign
  //     from a form they were only reading
  //   - never while a save is already in flight, or the first POST and its follow-up PUT
  //     race and create two campaigns
  const autosaveRef = useRef({ inFlight: false, last: "" });
  useEffect(() => {
    if (!isCreating || saving) return;
    if (!newCampaign.name.trim()) return;
    if (persistedIsLive) return;

    // Compare a snapshot, not object identity: setNewCampaign replaces the object on
    // every keystroke, and without this the debounce would re-fire on no-op renders.
    const snapshot = JSON.stringify(createLightweightDraft(newCampaign));
    if (snapshot === autosaveRef.current.last) return;

    const timer = setTimeout(async () => {
      if (autosaveRef.current.inFlight) return;
      autosaveRef.current.inFlight = true;
      try {
        await handleSaveCampaign(true, { quiet: true });
        autosaveRef.current.last = snapshot;
      } finally {
        autosaveRef.current.inFlight = false;
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [newCampaign, isCreating, saving, persistedIsLive]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Clear draft when successfully saved or explicitly discarded
  const clearDraft = () => {
    localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
    setHasDraft(false);
  };

  // Go to Creative Builder and be able to come back.
  //
  // Round 2: "create campaign > image selector > generate image > takes you to creative
  // builder, but doesn't link back to campaign, and the campaign draft is gone."
  //
  // The campaign is SAVED first, not stashed in localStorage, so there is a real
  // campaign to return to and an id to pass. Creatives shows a "Use in my campaign"
  // action while that id is present, and sends the merchant back here.
  const saveDraftAndGoToCreatives = async () => {
    if (!newCampaign.name.trim()) {
      toast.error("Give the campaign a name first", {
        description: "So there is something to come back to.",
      });
      return;
    }
    const id = editingCampaignId || await handleSaveCampaign(true, { quiet: true });
    if (!id) return;  // the save failed and already said why
    navigate(`/creatives?campaign=${id}&name=${encodeURIComponent(newCampaign.name)}`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Coming back from Creative Builder with ?edit=<id>: reopen that campaign in the form
  // so the merchant lands where they left, not on the campaign list.
  //
  // Waits for `campaigns` to load, because handleEditCampaign reads the campaign out of
  // it - firing on an empty list would silently do nothing and leave them on the list
  // anyway, which is the bug this exists to fix.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const wanted = searchParams.get("edit");
    if (!wanted || !campaigns.length) return;
    const campaign = campaigns.find((c) => c.id === wanted);
    if (campaign) {
      handleEditCampaign(campaign);
    } else {
      toast.error("That campaign could not be found");
    }
    // Clear it either way, so a refresh does not reopen the form and a failed lookup
    // does not keep complaining.
    setSearchParams({}, { replace: true });
  }, [searchParams, campaigns]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [campaignsRes, creativesRes, locationsRes, pricingRes, tierConfigRes, statesRes, accountRes, contentRes, offersRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers }),
        axios.get(`${API}/creatives`, { headers }),
        axios.get(`${API}/locations`, { headers }),
        axios.get(`${API}/settings/pricing`, { headers }),
        // DEFAULT_TIER_CONFIG is a hand-maintained duplicate of the backend's
        // CAMPAIGN_TIER_CONFIG and it WILL drift - the Interactive Map entitlement
        // already did, showing Silver a feature it does not get. Falling back silently
        // meant a merchant could be quoted prices and entitlements from stale local
        // constants with no sign anything was wrong, on the form that takes their money.
        // The fallback stays so the form still works; the failure is now visible.
        axios.get(`${API}/settings/tier-config`, { headers }).catch(() => {
          toast.error("Could not load current tier pricing - showing defaults. Refresh before purchasing.");
          return { data: { tiers: DEFAULT_TIER_CONFIG } };
        }),
        axios.get(`${API}/targeting/states`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/me`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/content`, { headers }).catch(() => ({ data: [] })),
        // The campaign panel shows a campaign's offers (round 9, TP-15). Allowed to
        // fail on its own: an offers outage must not empty the campaign list.
        axios.get(`${API}/offers`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCampaigns(campaignsRes.data);
      setCreatives(creativesRes.data);
      setContentList(contentRes.data || []);
      setOffers(offersRes.data || []);
      setLocations(locationsRes.data);
      setGlobalPricing(pricingRes.data);
      if (tierConfigRes.data?.tiers) setCampaignTierConfig(tierConfigRes.data.tiers);
      setAustralianStates(statesRes.data || []);
      setAccount(accountRes.data);
      // Returned as well as stored (round 11). settleAfterPayment polls this, and
      // state set here is not visible to the caller until the next render, so a poll
      // reading `campaigns` would compare the same stale array every time.
      return campaignsRes.data;
    } catch (error) {
      toast.error("Failed to fetch data");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Search for suburbs/postcodes
  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSearchResults([]);
      return;
    }
    
    setSearchingLocations(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/targeting/search-locations`, {
        headers,
        params: { query }
      });
      setLocationSearchResults(response.data || []);
    } catch (error) {
      console.error("Location search error:", error);
      setLocationSearchResults([]);
    } finally {
      setSearchingLocations(false);
    }
  };

  // Debounced location search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationSearch) {
        searchLocations(locationSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [locationSearch]);

  // A control this tier cannot use. Faded, and the cursor says so on hover - that
  // second half is round 9, TP-3: only Gender had it, because it is the one native
  // select in the row and a browser gives a disabled select the cursor for free.
  const LOCKED_CONTROL = "opacity-50 cursor-not-allowed";

  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    return toLocalDateString(new Date());
  };

  /**
   * What the auto end date actually means (round 9, TP-2).
   *
   * The field showed a bare date on Silver and Gold and a bare phrase on Bronze, so
   * neither told the whole story: a merchant looking at "10/09/2027" has to work out
   * what it was counted from, and one looking at "12 months from go-live" cannot see
   * when that lands. Say both.
   */
  const endDateNote = (duration, endAt) => {
    const months = { "1_month": 1, "3_months": 3, "6_months": 6, "12_months": 12 }[duration];
    if (!months) return null;
    const term = `${months} month${months === 1 ? "" : "s"} from go-live`;
    const on = endAt ? new Date(endAt) : null;
    if (!on || Number.isNaN(on.getTime())) return term;
    return `${term}, ${on.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  /**
   * What must be true before we charge. NOT before we save.
   *
   * These all used to run on the only button there was, which is why the form had no
   * Save: you could not persist a campaign until it was complete enough to pay for. A
   * draft exists precisely to be incomplete.
   *
   * Dates only. Tier, duration, creative and offers are checked by the backend's
   * assert_launch_ready, which is authoritative and names what is missing — duplicating
   * it here would give two sources of truth for the same refusal. The date rules have no
   * backend equivalent, so they stay.
   */
  const launchBlockers = () => {
    const problems = [];
    const today = getTodayDate();
    // Bronze start/end are set by Booma when the product goes live.
    if (newCampaign.tier && newCampaign.tier !== 'BRONZE') {
      if (!newCampaign.start_at) problems.push("a start date");
      else if (newCampaign.start_at < today) problems.push("a start date that is not in the past");
      if (!newCampaign.end_at) problems.push("an end date");
      else if (newCampaign.start_at && newCampaign.end_at < newCampaign.start_at) {
        problems.push("an end date after the start date");
      }
    }
    return problems;
  };

  /**
   * Persist the campaign. Never charges.
   *
   * `keepOpen` leaves the form open on the saved campaign, which is how offers become
   * reachable during creation: OfferManager needs a campaignId, and until now a new
   * campaign had none until it had been paid for.
   */
  // This page shows what is LIVE. Drafts moved to /campaigns/archived (#36): autosave
  // means every abandoned form-fill becomes one, and they were filling the page with
  // half-built cards among real campaigns - each carrying a pricing panel that reads as
  // though something is running.
  //
  // The count is still surfaced here, as a link, because a merchant must be able to see
  // that they have unfinished work without already knowing where to look.
  const activeCampaigns = campaigns.filter(c => c.status !== "DRAFT");
  const draftCount = campaigns.filter(c => c.status === "DRAFT").length;

  const handleSaveCampaign = async (keepOpen = false, { quiet = false } = {}) => {
    // A name is the only thing a draft genuinely needs — it is how the merchant finds it
    // again. Everything else can be filled in later, which is the point of a draft.
    if (!newCampaign.name.trim()) {
      toast.error("Give the campaign a name so you can find it again");
      return null;
    }

    setSaving(true);
    try {
      // Every chosen Content -> its per-placement rendition creatives (#8/P2-5, G-20).
      // Falls back to the stored ids so a campaign whose content has since been deleted
      // from the library still saves with what it had, rather than silently losing it.
      const picked = newCampaign.selectedContents || [];
      const creativeIds = picked.length
        ? picked.flatMap(ct => ct.rendition_creative_ids || [])
        : (newCampaign.creative_ids || []);
      
      // Build the campaign payload
      const payload = {
        name: newCampaign.name,
        description: newCampaign.description,
        tier: newCampaign.tier,  // Campaign-level tier selection
        objective: newCampaign.objective || null,
        surfaces: newCampaign.surfaces,
        start_at: newCampaign.start_at || null,
        end_at: newCampaign.end_at || null,
        duration: newCampaign.duration || null,  // Fixed duration
        budget_limit: newCampaign.tier === 'BRONZE' ? BRONZE_BUDGET_CAP : (newCampaign.budget_limit ? parseFloat(newCampaign.budget_limit) : null),
        pricing: newCampaign.pricing,
        creative_ids: creativeIds,
        // Deal configuration
        deal_available: newCampaign.deal_available,
        deal_description: newCampaign.deal_description || null,
        offer_text: newCampaign.offer_text || null,
        cta_text: newCampaign.cta_text || null,
        // Sponsored Trips add-on (#18). Quantity only; the server prices it.
        //
        // Zero rather than null, deliberately, and it is not the obvious choice: the
        // update route strips null values before it does anything, so a null is not
        // "remove this", it is "do not mention this" and the stored add-on survives.
        // A zero quantity is the only way a client can say NONE. See the boost payload
        // below, which had exactly that bug (round 9, TP-8).
        sponsored_trips: {
          quantity: newCampaign.sponsored_trips_enabled
            ? (parseInt(newCampaign.sponsored_trips_qty) || 0)
            : 0,
        },
        // Boost is priced server-side. `duration` is still sent because DiscoveryBoost
        // declares it and the stored sub-document records the period the entitlement
        // covers - it just does not price anything any more.
        //
        // Round 9, TP-8: this used to send null when the toggle was off, and null is
        // STRIPPED by the update route, so switching Boost off on a draft left the
        // stored sub-document in place. That is why the Feature Offer panel stayed
        // visible with the toggle off, and worse: launch prices the charge from the
        // stored document, so a draft that had Boost switched on and then off was
        // still charged for it. Quantity zero is how a client says none.
        discovery_boost: {
          duration: newCampaign.duration,
          quantity: newCampaign.discovery_boost_enabled
            ? (Number(newCampaign.discovery_boost_qty) || 1)
            : 0,
          image_source: newCampaign.discovery_boost_image || "business",
        },
        targeting_config: newCampaign.targeting.enabled ? {
          location_ids: newCampaign.targeting.location_ids,
          states: newCampaign.targeting.states,
          postcodes: newCampaign.targeting.postcodes,
          suburbs: newCampaign.targeting.suburbs,
          radius_override: newCampaign.targeting.radius_override,
          days: newCampaign.targeting.days,
          time_slots: newCampaign.targeting.time_slots,
          gender: newCampaign.targeting.gender,
          age_ranges: newCampaign.targeting.age_ranges,
          venue_types: newCampaign.targeting.venue_types,
          interests: newCampaign.targeting.interests,
          // Sent so one-chip-per-locality survives a reopen (#21). Without it the chips
          // re-split on edit, which is the reported bug returning.
          locality_pairs: newCampaign.targeting.locality_pairs || {}
        } : null
      };
      
      // Draft-then-pay: saving never charges. `keepOpen` is what makes offers reachable
      // during creation — the form stays open with editingCampaignId set, so
      // OfferManager finally has a campaignId instead of showing "Save the campaign
      // first" for the entire life of a new campaign.
      if (editingCampaignId) {
        // No confirm parameter any more (round 7, SP-1). Lowering a budget to or
        // below spend is refused outright and there is no way through it.
        await axios.put(`${API}/campaigns/${editingCampaignId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        clearDraft();
        await fetchData();
        if (!keepOpen) {
          setIsCreating(false);
          resetNewCampaign();
        }
        if (!quiet) toast.success(keepOpen ? "Draft saved" : "Campaign updated");
        return editingCampaignId;
      }

      const response = await axios.post(`${API}/campaigns`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { campaign } = response.data || {};
      clearDraft();
      await fetchData();
      if (keepOpen) {
        // Adopt the saved campaign so the form becomes an edit of it. Without this the
        // next Save would create a second campaign.
        setEditingCampaignId(campaign?.id || null);
        if (!quiet) {
          toast.success("Draft saved", {
            description: "You can add offers now, and launch when you are ready.",
          });
        }
      } else {
        setIsCreating(false);
        resetNewCampaign();
        if (!quiet) toast.success("Draft saved");
      }
      return campaign?.id || null;
    } catch (error) {
      // A budget at or below spend stops delivery. The server refuses once with the
      // figures so the merchant sees what they are about to do; this is not a blanket
      // block, because stopping a campaign this way is a legitimate thing to want.
      // The envelope keeps `detail` as a readable string and merges the structured
      // keys alongside it, so `code` and the figures sit at the top level of data.
      if (error.response?.status === 409
          && error.response?.data?.code === "BUDGET_STOPS_DELIVERY") {
        setBudgetStop({ ...error.response.data, keepOpen });
        return null;
      }

      // Handle Pydantic validation errors (422) which return an array
      const errorData = error.response?.data;
      let errorMessage = "Failed to create campaign";
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
      console.error("Campaign save error:", errorData);
      // Null, not undefined-by-omission: handleLaunch branches on it to decide whether
      // there is a campaign to charge for.
      return null;
    } finally {
      setSaving(false);
      // Round 9, TP-19. This used to sit on the success path only, so a refusal left
      // the "Save changes?" dialog sitting over the error toast with nothing on it
      // pointing at the problem. Joel's words were that he could not progress or
      // cancel. The dialog has asked its question either way, so it closes either way;
      // the toast carries the outcome.
      setConfirmSaveOpen(false);
    }
  };

  /**
   * Save, then charge. The only path that takes money for a campaign.
   *
   * Saving first is what makes this safe to press twice: the campaign is persisted
   * before Stripe is involved, so a failure anywhere after this point loses nothing.
   * Previously the campaign did not exist until the charge had been raised, so a
   * mid-flow failure left the merchant with a PAYMENT_FAILED record they had to hunt
   * for — or nothing at all.
   */
  const handleLaunch = async () => {
    const problems = launchBlockers();
    if (problems.length) {
      toast.error(`Before launching, set ${problems.join(", ")}.`);
      return;
    }

    const campaignId = await handleSaveCampaign(true);
    if (!campaignId) return;

    setSaving(true);
    try {
      const res = await axios.post(`${API}/campaigns/${campaignId}/launch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { client_secret, breakdown } = res.data || {};

      if (!client_secret) {
        // Nothing to charge — an admin acting for an advertiser, or a zero-cost tier.
        // The server has already taken it live.
        setIsCreating(false);
        setEditingCampaignId(null);
        resetNewCampaign();
        await fetchData();
        toast.success("Campaign is live");
        return;
      }

      setPaymentModal({
        open: true,
        clientSecret: client_secret,
        amount: breakdown?.total ?? 0,
        breakdown,
        campaignName: newCampaign.name,
      });
    } catch (error) {
      // The backend names what is missing (assert_launch_ready), so show it verbatim
      // rather than replacing it with a generic failure.
      toast.error(error.response?.data?.detail || "Could not start the launch");
    } finally {
      setSaving(false);
    }
  };

  // Resolve a stored /api/media/... path to an absolute backend URL (works on the
  // deployed site and localhost).
  const resolveMedia = (url) =>
    !url || url.startsWith("http") || url.startsWith("data:") ? url : `${API.replace(/\/api\/?$/, "")}${url}`;

  const openContentPicker = () => setCreativePickerOpen(true);

  // How many images this campaign's plan allows (G-20). Falls back to 1, which is what
  // every tier effectively had before the cap existed, so a stale tier config cannot
  // accidentally hand out an unlimited allowance.
  const creativeLimit = CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.max_creatives || 1;

  /** Toggle a Content in or out of the campaign, respecting the plan's allowance. */
  const selectContent = (ct) => {
    setNewCampaign(prev => {
      const current = prev.selectedContents || [];
      const already = current.some(c => c.id === ct.id);
      let next;
      if (already) {
        next = current.filter(c => c.id !== ct.id);
      } else if (current.length >= creativeLimit) {
        // At the allowance. With a cap of 1 the only sensible reading of a click is
        // "use this one instead", and refusing would leave the merchant at a dead end
        // with nothing to click. Above 1 the choice is theirs, so say what is in the way.
        if (creativeLimit === 1) {
          next = [ct];
        } else {
          toast.error(
            `${CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.name || newCampaign.tier} campaigns can use ${creativeLimit} images`,
            { description: "Remove one to add another, or move up a plan." }
          );
          return prev;
        }
      } else {
        next = [...current, ct];
      }
      return {
        ...prev,
        selectedContents: next,
        creative_ids: next.flatMap(c => c.rendition_creative_ids || []),
      };
    });
    // One image means one decision, so the dialog closes on it. With an allowance of
    // more it stays open, because picking three images should not mean reopening twice.
    if (creativeLimit === 1) setCreativePickerOpen(false);
  };

  const handleActivate = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign activated");
      fetchData();
    } catch (error) {
      // Activation can now be refused for a reason the merchant can act on — unpaid
      // (402) or already finished, so relaunch instead (409). Swallowing the detail
      // leaves them staring at "failed" with no idea what to do next.
      toast.error(error.response?.data?.detail || "Failed to activate campaign");
    }
  };

  /**
   * Refetch until the webhook has landed (round 11).
   *
   * Stripe confirms client-side the instant the card clears, but the campaign only
   * leaves DRAFT, and an add-on's pending quantity only becomes real, when the STRIPE
   * WEBHOOK reaches us a second or two later. A single refetch on success therefore
   * reads the campaign still pending, which is what Robin saw: "you have one
   * unfinished draft (nothing in drafts) and the new campaign doesn't automatically
   * appear", then a manual refresh showing it correctly.
   *
   * It also explains the contradiction in that report. The draft COUNTER and the
   * Drafts PAGE were read moments apart and the webhook landed in between, so the two
   * disagreed about the same campaign.
   *
   * Polls rather than patching the list locally. An optimistic update would be
   * instant and would lie whenever the webhook failed, which is the one case where
   * the merchant most needs the truth: they have been charged and the campaign has
   * not gone live.
   */
  const settleAfterPayment = async () => {
    const before = campaigns.filter(c => c.status === "DRAFT").length;
    // Roughly six seconds, front-loaded. A webhook that has not arrived by then is a
    // real problem and the merchant should see the unsettled state rather than a
    // spinner that never ends.
    const waits = [0, 400, 800, 1200, 1600, 2000];
    for (const wait of waits) {
      if (wait) await new Promise(r => setTimeout(r, wait));
      const fresh = await fetchData();
      if (!Array.isArray(fresh)) continue;
      if (fresh.filter(c => c.status === "DRAFT").length < before) return;
    }
  };

  // What Stripe will actually charge: the tier plus every add-on line item. Both
  // payment call sites used a bare tier_cost, which understated the charge whenever
  // an add-on existed.
  /**
   * What a paid campaign may no longer change (round 11, Robin).
   *
   * The DURATION is locked the moment money is taken. It always was - it is a priced
   * field and assert_repricing_allowed refuses it - but the form offered the control
   * anyway, so the merchant discovered the rule by being refused.
   *
   * The START DATE is different: it is locked only once it has ELAPSED. Delaying a
   * launch you have paid for is reasonable and changes nothing about the term; moving
   * the start of a campaign that is already running rewrites when it began while its
   * end date stays put.
   */
  const editingCampaign = editingCampaignId
    ? campaigns.find(c => c.id === editingCampaignId)
    : null;
  const isPaidCampaign = editingCampaign?.payment_status === "paid";
  const durationLocked = isPaidCampaign;
  const startDateLocked = isPaidCampaign && (() => {
    const started = editingCampaign?.start_at ? new Date(editingCampaign.start_at) : null;
    return Boolean(started) && !Number.isNaN(started.getTime()) && started <= new Date();
  })();

  const chargeTotalFor = (campaign) => (
    Number(campaign?.tier_cost || 0)
    + Number(campaign?.sponsored_trips?.total_cost || 0)
    + Number(campaign?.discovery_boost?.total_cost || 0)
  );



  /**
   * Launch a draft straight from its card, without opening the form.
   *
   * This is also the retry path for a declined card. There is no separate retry:
   * launching a draft whose payment failed reuses the same PaymentIntent when the amount
   * still matches, which the backend handles. Two endpoints raising an intent for the
   * same campaign is what produced the D7 undercharge, so there is now only one.
   */
  const handleLaunchCampaign = async (campaign) => {
    try {
      const res = await axios.post(`${API}/campaigns/${campaign.id}/launch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { client_secret, breakdown } = res.data || {};
      if (!client_secret) {
        await fetchData();
        toast.success("Campaign is live");
        return;
      }
      setPaymentModal({
        open: true,
        clientSecret: client_secret,
        amount: breakdown?.total ?? chargeTotalFor(campaign),
        breakdown,
        campaignName: campaign.name,
      });
    } catch (error) {
      // assert_launch_ready names the missing piece — "add a creative", not "failed".
      // Passing it through is the whole reason it says that.
      toast.error(error.response?.data?.detail || "Could not start the launch");
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign paused");
      fetchData();
    } catch (error) {
      // Show specific error message for Gold tier restriction
      const errorMsg = error.response?.data?.detail || "Failed to pause campaign";
      toast.error(errorMsg);
    }
  };

  // The AI deal suggester lived here, filling the legacy Deal Settings fields it was
  // written for. Both are gone: offers are built from the nine templates now, and a
  // suggester that writes free text into a shape nothing renders is worse than none.
  // OfferManager keeps its own suggester, which produces a template and a payload.

  // Open archive confirmation modal
  const openArchiveModal = (campaign) => {
    setCampaignToArchive(campaign);
    setArchiveModalOpen(true);
  };

  // Confirm archive action
  const confirmArchive = async () => {
    if (!campaignToArchive) return;
    
    setArchiving(true);
    try {
      // The server decides which of the two it does - it can see the ad ledger and we
      // cannot - so the confirmation reports its answer rather than our prediction.
      const res = await axios.delete(`${API}/campaigns/${campaignToArchive.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const wasDeleted = res.data?.deleted === true;
      const offersGone = res.data?.offers_deleted || 0;
      toast.success(
        wasDeleted
          ? `Draft deleted${offersGone ? ` (${offersGone} offer${offersGone === 1 ? "" : "s"} removed)` : ""}`
          : "Campaign archived successfully"
      );
      setArchiveModalOpen(false);
      setCampaignToArchive(null);
      fetchData();
    } catch (error) {
      toast.error(deletesOutright ? "Failed to delete draft" : "Failed to archive campaign");
    } finally {
      setArchiving(false);
    }
  };

  const handleEditCampaign = (campaign) => {
    // #21: load an existing campaign into the builder and save via PUT. Best-effort
    // mapping of the stored campaign shape back to the builder's form state.
    const ids = campaign.creative_ids || [];
    const tcfg = campaign.targeting_config || null;
    const def = getDefaultCampaign();
    setEditingCampaignId(campaign.id);
    setNewCampaign({
      ...def,
      name: campaign.name || "",
      description: campaign.description || "",
      tier: campaign.tier || "",
      objective: campaign.objective || "",
      surfaces: campaign.surfaces || def.surfaces,
      start_at: campaign.start_at ? String(campaign.start_at).split("T")[0] : "",
      end_at: campaign.end_at ? String(campaign.end_at).split("T")[0] : "",
      duration: campaign.duration || "",
      budget_limit: campaign.budget_limit != null ? String(campaign.budget_limit) : "",
      pricing: { ...def.pricing, ...(campaign.pricing || {}) },
      creative_ids: ids,
      // Reverse-map the stored rendition ids back to their Content (rendition ids are
      // unique per Content). filter, not find: a campaign can hold several (G-20).
      selectedContents: contentList.filter(
        ct => (ct.rendition_creative_ids || []).some(rid => ids.includes(rid))
      ),
      deal_available: !!campaign.deal_available,
      deal_description: campaign.deal_description || "",
      offer_text: campaign.offer_text || "",
      cta_text: campaign.cta_text || "Claim Deal",
      sponsored_trips_enabled: (campaign.sponsored_trips?.quantity || 0) > 0,
      sponsored_trips_qty: campaign.sponsored_trips?.quantity || 0,
      discovery_boost_enabled: !!campaign.discovery_boost,
      discovery_boost_qty: campaign.discovery_boost?.quantity || 1,
      discovery_boost_image: campaign.discovery_boost?.image_source || "business",
      targeting: tcfg ? { ...def.targeting, ...tcfg, enabled: tcfg.enabled ?? true } : def.targeting,
    });
    setIsCreating(true);
  };

  const resetNewCampaign = () => {
    setNewCampaign(getDefaultCampaign());
    setEditingCampaignId(null);
  };

  // Discard draft / cancel edit and reset form
  const discardDraft = () => {
    const wasEditing = !!editingCampaignId;
    clearDraft();
    resetNewCampaign();
    setIsCreating(false);
    toast.info(wasEditing ? "Edit cancelled" : "Draft discarded");
  };

  // Helper to get location targeting summary
  const getLocationTargetingSummary = () => {
    const parts = [];
    const t = newCampaign.targeting;
    
    if (t.location_ids.length > 0) {
      parts.push(`${t.location_ids.length} venue${t.location_ids.length > 1 ? 's' : ''}`);
    }
    if (t.states.length > 0) {
      parts.push(`${t.states.length} state${t.states.length > 1 ? 's' : ''}`);
    }
    if (t.postcodes.length > 0) {
      parts.push(`${t.postcodes.length} postcode${t.postcodes.length > 1 ? 's' : ''}`);
    }
    if (t.suburbs.length > 0) {
      parts.push(`${t.suburbs.length} suburb${t.suburbs.length > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'All Areas';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading campaigns...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Campaigns</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage your campaigns
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Campaigns</p>
                <p className="text-2xl font-bold">{campaigns.length}</p>
              </div>
              <Megaphone className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-500">
                  {campaigns.filter(c => c.status === "ACTIVE").length}
                </p>
              </div>
              <Play className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Paused <span className="text-xs">(Gold Campaigns)</span>
                </p>
                <p className="text-2xl font-bold text-amber-500">
                  {campaigns.filter(c => c.status === "PAUSED").length}
                </p>
              </div>
              <Pause className="h-8 w-8 opacity-50 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              {/* Links to where drafts now live (#36). */}
              <div>
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold text-muted-foreground">{draftCount}</p>
                {draftCount > 0 && (
                  <Link
                    to="/campaigns/archived"
                    className="text-xs text-primary underline hover:no-underline"
                    data-testid="drafts-link"
                  >
                    View drafts
                  </Link>
                )}
              </div>
              <Edit className="h-8 w-8 text-muted-foreground opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Widgets - Full Width */}
      <div className="space-y-4">
        {activeCampaigns.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="p-12 text-center">
              <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No live campaigns</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {draftCount > 0
                  ? "Create a campaign to get started, or pick up where you left off."
                  : "Create your first campaign to get started"}
              </p>
              {/* A merchant with drafts and an empty list must not be left wondering
                  where their work went (#36). */}
              {draftCount > 0 && (
                <Link
                  to="/campaigns/archived"
                  className="text-sm text-primary underline hover:no-underline"
                  data-testid="empty-state-drafts-link"
                >
                  You have {draftCount} unfinished draft{draftCount === 1 ? "" : "s"}
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          /* While editing, collapse the card list so the builder below is the single
             editing surface for that campaign (no duplicate "new" widget). (#21 fix) */
          (editingCampaignId ? [] : activeCampaigns).map(campaign => {
            // The campaign's CONTENT, not its flattened renditions (round 9, TP-15).
            // Same reverse-map handleEditCampaign uses, so the panel and the edit form
            // resolve a campaign's creatives identically.
            const campaignCreativeIds = campaign.creative_ids || [];
            const campaignContent = contentList.filter(
              ct => (ct.rendition_creative_ids || []).some(rid => campaignCreativeIds.includes(rid))
            );
            const campaignOffers = offers.filter(o => o.campaign_id === campaign.id);
            const targeting = campaign.targeting_config || {};
            const pricing = campaign.pricing || {};
            const campaignTier = campaign.tier || "BRONZE";
            const tierConfig = CAMPAIGN_TIER_CONFIG[campaignTier] || CAMPAIGN_TIER_CONFIG.BRONZE;
            
            return (
              <Card 
                key={campaign.id} 
                className="bg-card border-border hover:border-primary/50 transition-all hover:shadow-lg group"
                data-testid={`campaign-widget-${campaign.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {/* Left Section - Campaign Info & Creatives */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={statusColors[campaign.status]}>
                              {statusLabels[campaign.status] || campaign.status}
                            </Badge>
                            {/* A declined card is a property of the draft, not a status
                                of its own (decision D1). The reason comes from the
                                webhook - "your card was declined" and "insufficient
                                funds" are different problems, and the retired
                                PAYMENT_FAILED status never carried either. */}
                            {campaign.status === "DRAFT" && campaign.payment_status === "failed" && (
                              <Badge className="bg-red-500/15 text-red-600 border-red-500/30">
                                Payment declined
                              </Badge>
                            )}
                            {campaign.status === "DRAFT" && campaign.payment_status === "pending"
                              && campaign.payment_intent_id && (
                              <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30">
                                Payment in progress
                              </Badge>
                            )}
                            <Badge className={`${
                              campaignTier === 'GOLD' ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                              campaignTier === 'SILVER' ? 'bg-slate-400/20 text-slate-600 border-slate-400/30' :
                              'bg-orange-700/20 text-orange-700 border-orange-700/30'
                            }`}>
                              {campaignTier}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {campaign.surfaces?.map(s => s.replace("_", " ")).join(" • ")}
                            </span>
                            {/* spec 7.3.6: a campaign that has spent its budget stops being
                                delivered, so say so on the card. Without this a merchant sees
                                delivery stop and has to work out why. */}
                            {campaign.budget_state?.exhausted && (
                              <Badge className="bg-red-500/20 text-red-600 border-red-500/30">
                                BUDGET SPENT
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-xl font-bold">{campaign.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {campaign.description || "No description"}
                          </p>
                          {campaign.status === "DRAFT" && campaign.payment_status === "failed" && (
                            <p className="text-xs text-red-600 mt-1">
                              {campaign.last_payment_error
                                || "Your card was declined."}{" "}
                              Your campaign is saved - use Launch &amp; pay to try again.
                            </p>
                          )}
                          {/* spec 7.3.6: spend against budget, so the remaining figure is visible
                              rather than inferred from delivery stopping. Shown only when a budget is
                              set - a blank budget is uncapped (decision B1). */}
                          {campaign.budget_state?.limit != null && (() => {
                            const b = campaign.budget_state;
                            const pct = Math.min(100, b.fraction);
                            const bar = b.exhausted ? "bg-red-500"
                              : b.fraction >= 80 ? "bg-amber-500" : "bg-emerald-500";
                            return (
                              <div className="mt-2 max-w-xs" data-testid="campaign-budget">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">
                                    ${b.spent.toFixed(2)} of ${b.limit.toFixed(2)} spent
                                  </span>
                                  <span className={b.exhausted ? "text-red-600" : "text-muted-foreground"}>
                                    {b.exhausted ? "No budget remaining" : `$${b.remaining.toFixed(2)} left`}
                                  </span>
                                </div>
                                <div className="h-1.5 mt-1 rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })()}
                          {/* Effective Date Range - Prominent Display */}
                          {(campaign.start_at || campaign.end_at) && (() => {
                            const now = new Date();
                            const endDate = campaign.end_at ? new Date(campaign.end_at) : null;
                            const startDate = campaign.start_at ? new Date(campaign.start_at) : null;
                            const isExpired = endDate && endDate < now;
                            const isNotStarted = startDate && startDate > now;
                            const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : null;
                            
                            return (
                              <div className={`mt-3 p-2 rounded-lg border ${
                                isExpired ? 'bg-red-500/10 border-red-500/30' : 
                                isNotStarted ? 'bg-blue-500/10 border-blue-500/30' :
                                daysRemaining && daysRemaining <= 7 ? 'bg-amber-500/10 border-amber-500/30' : 
                                'bg-green-500/10 border-green-500/30'
                              }`}>
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2">
                                    <Clock className={`h-4 w-4 ${
                                      isExpired ? 'text-red-500' : 
                                      isNotStarted ? 'text-blue-500' :
                                      daysRemaining && daysRemaining <= 7 ? 'text-amber-500' : 
                                      'text-green-500'
                                    }`} />
                                    <span className="text-sm font-medium">
                                      {campaign.start_at ? new Date(campaign.start_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : 'No start'}
                                      {' → '}
                                      {campaign.end_at ? new Date(campaign.end_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'}
                                    </span>
                                  </div>
                                  {endDate && (
                                    <Badge className={`text-xs ml-2 ${
                                      isExpired ? 'bg-red-500/20 text-red-600 border-red-500/30' :
                                      isNotStarted ? 'bg-blue-500/20 text-blue-600 border-blue-500/30' :
                                      daysRemaining <= 7 ? 'bg-amber-500/20 text-amber-600 border-amber-500/30' :
                                      'bg-green-500/20 text-green-600 border-green-500/30'
                                    }`}>
                                      {isExpired ? 'Expired' : 
                                       isNotStarted ? `Starts in ${Math.ceil((startDate - now) / (1000 * 60 * 60 * 24))} days` :
                                       `${daysRemaining} days left`}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditCampaign(campaign)} data-testid="edit-campaign-btn">
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit Campaign
                            </DropdownMenuItem>
                            {campaign.status === "ACTIVE" && (
                              campaignTier === "GOLD" ? (
                                <DropdownMenuItem onClick={() => handlePause(campaign.id)}>
                                  <Pause className="mr-2 h-4 w-4" />
                                  Pause Campaign
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem disabled className="text-muted-foreground cursor-not-allowed">
                                  <Pause className="mr-2 h-4 w-4 opacity-50" />
                                  Pause (Gold Tier Only)
                                </DropdownMenuItem>
                              )
                            )}
                            {campaign.status === "PAUSED" && (
                              <DropdownMenuItem onClick={() => handleActivate(campaign.id)}>
                                <Play className="mr-2 h-4 w-4" />
                                Resume Campaign
                              </DropdownMenuItem>
                            )}
                            {/* A draft is launched, not activated. Activate is only for
                                resuming a pause - it refuses an unpaid campaign with 402,
                                so pointing a draft at it was a dead end. */}
                            {campaign.status === "DRAFT" && (
                              <DropdownMenuItem onClick={() => handleLaunchCampaign(campaign)}>
                                <CreditCard className="mr-2 h-4 w-4" />
                                {campaign.payment_status === "failed" ? "Retry payment" : "Launch & pay"}
                              </DropdownMenuItem>
                            )}
                            {/* Two genuinely different actions. An unpaid draft is
                                DELETED - it and its offers are gone - and anything with
                                money or history behind it is archived. Round 2 asked to
                                "delete a campaign once made/saved as a draft" and found
                                only Archive; renaming the label was the first half of
                                that answer and made the button lie until the action
                                followed it. deleteRule is the same test the server
                                applies, kept beside it deliberately. */}
                            <DropdownMenuItem
                              onClick={() => openArchiveModal(campaign)}
                              className={deleteRule(campaign)
                                ? "text-destructive focus:text-destructive cursor-pointer"
                                : "text-amber-600 focus:text-amber-600 cursor-pointer"}
                              data-testid="archive-campaign-btn"
                            >
                              {deleteRule(campaign)
                                ? <Trash2 className="mr-2 h-4 w-4" />
                                : <Archive className="mr-2 h-4 w-4" />}
                              {deleteRule(campaign) ? "Delete draft" : "Archive"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* Creatives (round 9, TP-15).
                          This used to show campaignCreatives[0] as "Banner" and [1] as
                          "Panel". Those are RENDITIONS of one Content, and Banner was
                          retired in round 2, so the panel invented two placements out
                          of an arbitrary slice of a flat list and showed images the
                          merchant never chose for this campaign. It now shows the
                          Content itself, reverse-mapped from the stored rendition ids
                          exactly as handleEditCampaign does. */}
                      <div className="mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">
                          Creatives
                        </p>
                        {campaignContent.length > 0 ? (
                          <div className="flex flex-wrap gap-3">
                            {campaignContent.map(ct => (
                              <div key={ct.id} className="w-28 space-y-1">
                                <div className="aspect-square rounded-lg overflow-hidden bg-secondary border border-border flex items-center justify-center">
                                  {ct.master_url ? (
                                    <img
                                      src={resolveMedia(ct.master_url)}
                                      alt={ct.name || "Creative"}
                                      className="w-full h-full object-contain"
                                    />
                                  ) : (
                                    <Image className="h-6 w-6 text-muted-foreground" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate" title={ct.name}>
                                  {ct.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-24 rounded-lg bg-secondary/30 border border-dashed border-border flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">No creatives on this campaign</span>
                          </div>
                        )}
                      </div>

                      {/* Audience (round 9, TP-7).
                          Three of these eight cells read fields the model does not
                          have: venue_type and age_range are singular, and
                          CampaignTargeting has carried venue_types and age_ranges
                          since they became multi-select. Both were therefore always
                          undefined, so every campaign ever made displayed "Any" and
                          "All ages" whatever the merchant chose. interests had the
                          right name but is a list, and a list rendered into JSX comes
                          out as "cafesbars".

                          Joel filed it as wording. The summaries now live in
                          lib/audienceSummary.js so the next rename breaks the form and
                          the panel together rather than silently only one. */}
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Audience</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            ["Location", summariseLocations(targeting)],
                            ["Days", summariseDays(targeting)],
                            ["Time", summariseTimes(targeting)],
                            ["Radius", summariseRadius(targeting)],
                            ["Gender", summariseGender(targeting)],
                            ["Age Range", summariseAges(targeting)],
                            ["Venue Type", summariseVenueTypes(targeting)],
                            ["Interests", summariseInterests(targeting)],
                          ].map(([label, value]) => (
                            <div key={label} className="p-2 rounded-lg bg-secondary/20">
                              <p className="text-xs text-muted-foreground">{label}</p>
                              <p className="text-sm font-medium" title={value}>{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Offers (round 9, TP-15).
                          This block used to read campaign.deal_available, offer_text
                          and deal_description - the legacy scalars the Offer model
                          replaced - so a campaign with five real offers showed one
                          made-up "Special Offer" or nothing at all. */}
                      {campaignOffers.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium flex items-center gap-2">
                            <Gift className="h-4 w-4 text-amber-500" />
                            Offers ({campaignOffers.length})
                          </p>
                          {/* Round 12, FP-6: "offer are all different sizes and look
                              crap now. They all must be consistent size".
                              A flex-wrap row of chips, each sized to its own contents,
                              so "20% Off" was narrow, "Buy One Get One: Margherita
                              Pizza" was wide, and one carrying an image was taller than
                              one without. A grid gives every offer the same cell, and
                              the image box is always rendered so the text starts at the
                              same place on every card whether there is an image or not.
                              FP-20 asks the image to earn its space: 40px, up from 32,
                              and no longer the only thing deciding the row's height. */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
                            {campaignOffers.map(offer => (
                              <div
                                key={offer.id}
                                className="flex items-center gap-2.5 p-2 pr-3 rounded-lg bg-amber-500/10 border border-amber-500/20 min-h-[3.25rem]"
                              >
                                <div className="h-10 w-10 shrink-0 rounded overflow-hidden bg-muted/60 flex items-center justify-center">
                                  {offer.creative_url ? (
                                    <img
                                      src={resolveMedia(offer.creative_url)}
                                      alt=""
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <Gift className="h-4 w-4 text-muted-foreground/50" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate" title={offer.display_value}>{offer.display_value}</p>
                                  {offer.source === "DISCOVERY_BOOST" && (
                                    <p className="text-[10px] text-purple-600">Feature Offer</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Metrics (round 9, TP-16 and TP-17).
                        The same shape as the setup form, minus the toggles: price
                        BENEATH the metric rather than beside it, the info buttons
                        back, and no "All Types" chip, which was a label for nothing
                        and squashed the panel it sat in. "Enabled Pricing" is now
                        "Enabled Metrics", matching the vocabulary the form uses.

                        Both info icons sit on the right where the toggle would be, so
                        they line up with each other. That is TP-17: with the toggles
                        gone there is nothing else for them to align to. */}
                    <div className="w-56 shrink-0 border-l border-border pl-6">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-medium">Enabled Metrics</p>
                      <div className="space-y-2">
                        {/* Views. Always delivered; charged only where the tier says so
                            (round 7, SP-8). */}
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                          <span className="text-xs font-medium text-blue-600">Views</span>
                          {tierConfig.features?.cpm_enabled ? (
                            <>
                              <p className="text-lg font-bold mt-1">${((globalPricing?.cpm_rate || 120) / 1000).toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">per view</p>
                            </>
                          ) : (
                            <p className="text-xs text-muted-foreground mt-1">
                              Included in your campaign cost
                            </p>
                          )}
                        </div>

                        {pricing.cpc_enabled && (
                          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium text-green-600">Clicks</span>
                              <ClicksInfoButton />
                            </div>
                            <p className="text-lg font-bold mt-1">${globalPricing?.cpc_rate?.toFixed(2) || "0.35"}</p>
                            <p className="text-xs text-muted-foreground">per click</p>
                          </div>
                        )}

                        {/* Two legs, not three. Prompted was absorbed into Direct on
                            2026-09-03 and bills at the Direct rate, so quoting a
                            separate Prompted price advertised a charge that can no
                            longer occur. */}
                        {(pricing.cpdc_direct_enabled ||
                          pricing.cpdc_proximity_enabled || pricing.cpdc_prompted_enabled) && (
                          <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium text-purple-600">Delivered Customers</span>
                              <CPDCInfoButton />
                            </div>
                            <div className="mt-2 pt-2 border-t border-purple-500/20 space-y-1">
                              <div className="text-xs flex justify-between">
                                <span className="text-muted-foreground">Direct To Door</span>
                                <span className="font-bold text-purple-600">${globalPricing?.cpdc_direct_rate?.toFixed(2) || "8.00"}</span>
                              </div>
                              <div className="text-xs flex justify-between">
                                <span className="text-muted-foreground">Nearby Influenced</span>
                                <span className="font-bold text-purple-600">${globalPricing?.cpdc_proximity_rate?.toFixed(2) || "5.00"}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {!pricing.cpc_enabled && !pricing.cpdc_direct_enabled &&
                         !pricing.cpdc_proximity_enabled && !pricing.cpdc_prompted_enabled && (
                          <p className="text-xs text-muted-foreground text-center py-2">Views only</p>
                        )}
                      </div>

                      {/* Campaign Budget */}
                      {campaign.budget_limit && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Campaign Budget</p>
                          <div className="p-2 rounded-lg bg-pink-500/10 border border-pink-500/20">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-pink-600">Maximum Spend</span>
                              <span className="text-sm font-bold">${campaign.budget_limit.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* New Campaign Creation Widget */}
        {isCreating ? (
          <Card className="bg-card border-primary border-2">
            <CardContent className="p-6">
              <div className="flex gap-6">
                {/* Left Section - Campaign Info & Creatives */}
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30">{editingCampaignId ? "EDITING" : "NEW"}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Round 12, FP-31. Joel typed a typo into this field during
                            campaign creation and nothing underlined it. Explicit rather
                            than relying on the browser default, which differs between
                            browsers for a text input and is off in some. The dictionary
                            is now en-AU for the whole document (public/index.html), so
                            "favourite" no longer flags as a misspelling either. */}
                        <Input
                          spellCheck="true"
                          value={newCampaign.name}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Add Campaign Name"
                          className="text-xl font-bold border-0 border-b rounded-none px-0 focus-visible:ring-0"
                        />
                        {/* spec 7.2: pick a heading from the menu, or type your own. */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="shrink-0 text-xs text-muted-foreground" data-testid="campaign-name-presets">
                              Presets
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {CAMPAIGN_NAME_PRESETS.map((preset) => (
                              <DropdownMenuItem
                                key={preset}
                                onClick={() => setNewCampaign(prev => ({ ...prev, name: preset }))}
                              >
                                {preset}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {/* HIDDEN, round 2 (2026-09-02). Not deleted.
                          =====================================================
                          The objective select is built and works - it validates against
                          the tier, carries help text and an info button, and saves. It is
                          hidden because what it MEANS is unsettled: round 2 asked to
                          "workshop out exactly what the objective drop down is doing", and
                          separately noted that picking Awareness reads as giving up
                          clicks, saves and redeems when it does no such thing.

                          Two things have to be decided before it comes back:
                            - does an objective CHANGE delivery? Today it changes nothing
                              (spec 7.4.8 optimisation is unbuilt), so the copy has to
                              promise nothing - or the optimiser has to land first.
                            - one objective or several? Round 2 floated multiple, with
                              tier-appropriate defaults chosen for the merchant rather
                              than by them.

                          Until then a campaign saves with objective: "" and every
                          entitlement check treats that as "not set", which is already the
                          tested path. Set SHOW_CAMPAIGN_OBJECTIVE to true to restore it -
                          the whole block below is intact. */}
                      {SHOW_CAMPAIGN_OBJECTIVE && (() => {
                        const tierHasOffers = (CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.max_offers ?? 0) > 0;
                        const chosen = CAMPAIGN_OBJECTIVES.find((o) => o.value === newCampaign.objective);
                        return (
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-1.5">
                            <Select
                              value={newCampaign.objective || "__none__"}
                              onValueChange={(v) => setNewCampaign(prev => ({ ...prev, objective: v === "__none__" ? "" : v }))}
                            >
                              <SelectTrigger className="h-8 w-[240px] text-sm" data-testid="campaign-objective">
                                <SelectValue placeholder="Choose a campaign objective" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">No objective yet</SelectItem>
                                {CAMPAIGN_OBJECTIVES.map((o) => (
                                  <SelectItem
                                    key={o.value}
                                    value={o.value}
                                    disabled={o.needsOffers && !tierHasOffers}
                                  >
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {/* spec 7.0.7: explain what each objective is for. The closing
                                line is deliberate - §7.4.8 optimisation is not built, so the
                                objective does not currently change delivery, and copy that
                                implied otherwise would be selling something that does not
                                exist. Remove that sentence when the optimiser lands. */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button" variant="ghost" size="icon"
                                  className="h-7 w-7 text-muted-foreground"
                                  aria-label="What do campaign objectives do?"
                                  data-testid="objective-help"
                                >
                                  <Info className="h-3.5 w-3.5" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-3" align="start">
                                <p className="text-xs font-medium mb-2">Campaign objectives</p>
                                <div className="space-y-1.5">
                                  {CAMPAIGN_OBJECTIVES.map((o) => (
                                    <div key={o.value} className="text-[11px] leading-snug">
                                      <span className="font-medium">{o.label}</span>
                                      <span className="text-muted-foreground"> - {o.help}</span>
                                      {o.needsOffers && (
                                        <span className="text-muted-foreground"> Needs a clickable offer, so Silver or Gold.</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-2 pt-2 border-t">
                                  Your objective records what this campaign is for and is shown
                                  across your reporting. It does not change how ads are delivered
                                  yet - automatic optimisation is still to come.
                                </p>
                              </PopoverContent>
                            </Popover>
                            </div>
                            {chosen && (
                              <p className="text-[11px] text-muted-foreground">{chosen.help}</p>
                            )}
                            {newCampaign.tier && !tierHasOffers && (
                              <p className="text-[11px] text-muted-foreground">
                                {newCampaign.tier.charAt(0) + newCampaign.tier.slice(1).toLowerCase()} runs a
                                static awareness creative with no clickable offer.{" "}
                                {newCampaign.tier.charAt(0) + newCampaign.tier.slice(1).toLowerCase()} is limited
                                to the Awareness objective.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                      <Input
                        spellCheck="true"
                        value={newCampaign.description}
                        onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Campaign description (optional)"
                        className="text-sm text-muted-foreground mt-2 border-0 border-b rounded-none px-0 focus-visible:ring-0"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={discardDraft}
                      title={editingCampaignId ? "Cancel edit" : "Discard draft"}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Draft indicator */}
                  {hasDraft && (
                    <div className="mb-4 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2">
                      <Save className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-amber-600">Draft auto-saved</span>
                    </div>
                  )}

                  {/* Tier BEFORE content (round 3, 7.2.1). The tier decides what the
                      campaign can hold - whether it has offers at all, how many, which
                      targeting is available - so choosing artwork first and the tier
                      after inverts the dependency. */}
                  {/* Campaign Tier Selection - Horizontal Layout with Checkboxes */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Campaign Tier <span className="text-red-500">*</span></p>
                        <p className="text-xs text-muted-foreground mt-1">Select your campaign tier - subscription paid at start of campaign</p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-foreground gap-1 h-7 px-2"
                        onClick={() => setTiersExpanded(prev => !prev)}
                      >
                        {tiersExpanded ? 'Hide Details' : 'View Details'}
                        {tiersExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Round 12, FP-14. A PAID campaign shows only the tier it
                          bought. The backend has always refused a change - tier is a
                          priced field and assert_repricing_allowed returns 409 - so
                          the other two were options in name only, and offering them
                          taught the merchant the rule by refusing them. */}
                      {Object.entries(CAMPAIGN_TIER_CONFIG)
                        .filter(([tierKey]) => !isPaidCampaign || tierKey === newCampaign.tier)
                        .map(([tierKey, config]) => {
                        const isSelected = newCampaign.tier === tierKey;
                        // `enabled` is admin-controlled in /admin/tier-pricing. When false,
                        // the card stays visible (so prospects see what's coming) but is
                        // greyed out, can't be clicked, and shows a "Coming Soon" badge.
                        const isDisabled = config.enabled === false;
                        
                        const tierColors = {
                          GOLD: { border: 'border-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-600', icon: '👑', checkAccent: 'data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500' },
                          SILVER: { border: 'border-slate-400', bg: 'bg-slate-400/10', text: 'text-slate-600', icon: '🥈', checkAccent: 'data-[state=checked]:bg-slate-500 data-[state=checked]:border-slate-500' },
                          BRONZE: { border: 'border-orange-700', bg: 'bg-orange-700/10', text: 'text-orange-700', icon: '🥉', checkAccent: 'data-[state=checked]:bg-orange-700 data-[state=checked]:border-orange-700' }
                        };
                        const colors = tierColors[tierKey];
                        
                        // Matches the backend: tier_cost = price_monthly x duration_months.
                        const DURATION_MONTHS = { "1_month": 1, "3_months": 3, "6_months": 6, "12_months": 12 };
                        // Bronze is fixed at 12 months and has no other option, so its
                        // price does not wait for a duration to be chosen.
                        const priceMonths = tierKey === 'BRONZE' ? 12 : (DURATION_MONTHS[newCampaign.duration] || 0);
                        const tierPrice = (config.price_monthly || 0) * priceMonths;

                        // A price is only shown on the tier the merchant has SELECTED, and
                        // only once the duration it depends on is known.
                        //
                        // Every card used to price itself off whatever duration was set,
                        // whichever tier that duration belonged to - so three prices were
                        // on screen and two of them answered a question nobody asked.
                        // Bronze was the worst of it: always showing its 12-month total,
                        // because that is its only term, so a merchant pricing a 1-month
                        // Silver saw Bronze's $600 sitting beside it.
                        const isSelectedTier = newCampaign.tier === tierKey;
                        const showPrice = isSelectedTier && tierPrice > 0;

                        const handleSelectTier = () => {
                          if (isDisabled) return;
                          const tierFeatures = config.features;

                          // A downgrade silently discards whatever the lower tier cannot
                          // hold. It always did — this just says so. A merchant who loses
                          // their interest targeting to a tier change should find out
                          // now, not from a campaign that stops reaching the right people.
                          const t = newCampaign.targeting || {};
                          const chose = (v) => (v || []).some((x) => x && x !== "any");
                          const dropped = [];
                          if (!tierFeatures.advanced_targeting && tierFeatures.basic_targeting) {
                            if (chose(t.venue_types)) dropped.push("venue types");
                            if (chose(t.interests)) dropped.push("interests");
                          }
                          if (!tierFeatures.basic_targeting && t.enabled) {
                            dropped.push("all audience targeting");
                          }
                          if (!tierFeatures.deals_enabled && newCampaign.deal_available) {
                            dropped.push("deals");
                          }
                          if (!tierFeatures.cpc_enabled && newCampaign.pricing?.cpc_enabled) {
                            dropped.push("cost-per-click");
                          }
                          if (!tierFeatures.cpdc_enabled && (
                            newCampaign.pricing?.cpdc_direct_enabled
                            || newCampaign.pricing?.cpdc_proximity_enabled
                            || newCampaign.pricing?.cpdc_prompted_enabled
                          )) {
                            dropped.push("cost-per-delivered-customer");
                          }
                          if (tierKey === "BRONZE" && newCampaign.duration && newCampaign.duration !== "12_months") {
                            dropped.push("your chosen duration - Bronze runs 12 months");
                          }
                          if (dropped.length) {
                            toast.warning(`${config.name} does not support ${dropped.join(", ")}.`, {
                              description: "Cleared from this campaign. Switch back before saving to keep it.",
                            });
                          }

                          setNewCampaign(prev => {
                            const needsDurationReset = tierKey === 'BRONZE' && prev.duration !== '12_months';
                            return {
                              ...prev,
                              tier: tierKey,
                              duration: needsDurationReset ? '12_months' : prev.duration,
                              pricing: {
                                cpm_enabled: true,
                                cpc_enabled: tierFeatures.cpc_enabled ? prev.pricing.cpc_enabled : false,
                                cpdc_direct_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_direct_enabled : false,
                                cpdc_proximity_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_proximity_enabled : false,
                                cpdc_prompted_enabled: tierFeatures.cpdc_enabled ? prev.pricing.cpdc_prompted_enabled : false,
                              },
                              deal_available: tierFeatures.deals_enabled ? prev.deal_available : false,
                              targeting: tierFeatures.basic_targeting ? {
                                ...prev.targeting,
                                venue_types: tierFeatures.advanced_targeting ? prev.targeting.venue_types : ["any"],
                                interests: tierFeatures.advanced_targeting ? prev.targeting.interests : ["any"],
                              } : {
                                ...prev.targeting,
                                enabled: false,
                                location_ids: [],
                                states: [],
                                postcodes: [],
                                suburbs: [],
                                locality_pairs: {},
                                days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
                                time_slots: ["morning", "afternoon", "evening", "night"],
                                gender: "all",
                                age_ranges: ["all"],
                                venue_types: ["any"],
                                interests: ["any"],
                              }
                            };
                          });
                        };

                        return (
                          <div
                            key={tierKey}
                            className={`rounded-lg border-2 transition-all overflow-hidden ${
                              isDisabled
                                ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed'
                                : isSelected
                                  ? `${colors.border} ${colors.bg} cursor-pointer`
                                  : 'border-border hover:border-muted-foreground/40 cursor-pointer'
                            }`}
                            data-testid={`tier-${tierKey.toLowerCase()}`}
                            onClick={handleSelectTier}
                            title={isDisabled ? `${config.name} tier is coming soon` : undefined}
                          >
                            {/* Tier Header - Always visible */}
                            <div className={`p-3 ${isSelected && !isDisabled ? colors.bg : ''}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2.5 min-w-0">
                                  <Checkbox
                                    checked={isSelected && !isDisabled}
                                    onCheckedChange={handleSelectTier}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={isDisabled}
                                    className={`mt-0.5 ${colors.checkAccent}`}
                                    data-testid={`select-tier-${tierKey.toLowerCase()}`}
                                  />
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-base leading-none">{colors.icon}</span>
                                      <span className={`font-bold text-sm ${colors.text}`}>{config.name}</span>
                                      {isDisabled && (
                                        <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30 text-[9px] px-1.5 py-0">
                                          Coming Soon
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{config.tagline}</p>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  {config.is_free ? (
                                    <Badge className="bg-green-500/20 text-green-600 border-green-500/30 text-[10px] px-1.5 py-0">Free</Badge>
                                  ) : showPrice ? (
                                    <span
                                      className={`font-bold text-sm tabular-nums ${isDisabled ? 'text-muted-foreground' : colors.text}`}
                                      data-testid={`tier-price-${tierKey.toLowerCase()}`}
                                    >
                                      ${tierPrice.toLocaleString()}
                                    </span>
                                  ) : null}
                                  {/* Only the selected tier is told what it is waiting for.
                                      Prompting every card to "select duration" is three
                                      instructions for one decision. */}
                                  {!config.is_free && !isDisabled && isSelectedTier && !showPrice && (
                                    <p className="text-[9px] text-muted-foreground mt-0.5">
                                      select duration
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Expanded Feature Details (single toggle for all) */}
                            {tiersExpanded && (
                              <div className="px-3 pb-3 pt-2 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
                                <p className="text-xs text-muted-foreground mb-2">{config.description}</p>
                                {/* Included first, then excluded - round 2 asked for the
                                    ticks together at the top rather than interleaved, so
                                    a merchant can read what they GET without filtering
                                    out what they do not.

                                    Driven from TIER_FEATURE_ROWS rather than ten
                                    hand-written ternaries. That duplication is how the
                                    old delivered-customer label survived the rename here:
                                    it was typed twice per row, ten rows deep. */}
                                <div className="space-y-0.5">
                                  {partitionTierFeatures(config.features).map(({ key, label, included }) => (
                                    <p
                                      key={key}
                                      className={included
                                        ? "text-[11px] text-green-600"
                                        : "text-[11px] text-muted-foreground/60"}
                                    >
                                      {included ? "✓" : "✗"} {label}
                                    </p>
                                  ))}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-2">
                                  Max {config.max_campaigns_per_account} campaign(s) per account
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Campaign Content. Up to max_creatives images (round 4, G-20:
                      Bronze 1, Silver 3, Gold 5), each auto-adapted to every placement
                      (#8/P2-5). Which offer uses which image is a separate piece of
                      work - the offer does not carry a creative reference yet. */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Campaign Content</p>
                      <p className="text-xs text-muted-foreground">
                        {creativeLimit === 1
                          ? "Pick one - we adapt it to every placement"
                          : `${(newCampaign.selectedContents || []).length} of ${creativeLimit} images`}
                      </p>
                    </div>

                    {/* Horizontal, not a stacked list (round 7, SP-28): "campaign
                        content/creatives to go horinzontal not vertical takes up way to
                        much space and horzintally uses space better". With Gold holding
                        five, a full-width row each pushed everything below it off the
                        screen. */}
                    {(newCampaign.selectedContents || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(newCampaign.selectedContents || []).map((ct) => (
                          <div
                            key={ct.id}
                            className="relative w-28 rounded-lg overflow-hidden bg-secondary border border-primary"
                            data-testid="campaign-content-slot"
                          >
                            <div
                              className="aspect-square w-full bg-muted/40 flex items-center justify-center cursor-pointer"
                              onClick={openContentPicker}
                              title="Click to pick a different image"
                            >
                              {ct.master_url ? (
                                <img src={resolveMedia(ct.master_url)} alt={ct.name} className="w-full h-full object-contain" />
                              ) : <Image className="h-6 w-6 text-muted-foreground" />}
                            </div>
                            <p className="text-[11px] font-medium truncate px-1.5 py-1" title={ct.name}>
                              {ct.name}
                            </p>
                            {/* Removable in place. Deselecting in the picker works too,
                                but wanting one of five gone should not mean opening a
                                dialog to do it. */}
                            <button
                              type="button"
                              onClick={() => selectContent(ct)}
                              title="Remove this image"
                              className="absolute top-1 right-1 rounded-full bg-background/90 p-0.5 text-muted-foreground hover:text-destructive"
                              data-testid={`remove-content-${ct.id}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {(newCampaign.selectedContents || []).length < creativeLimit && (
                      <div
                        className="rounded-lg bg-secondary/30 border-2 border-dashed border-border hover:border-primary/50 transition-colors cursor-pointer flex flex-col items-center justify-center py-6"
                        onClick={openContentPicker}
                        data-testid="select-content-btn"
                      >
                        <Library className="h-6 w-6 text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground">
                          {(newCampaign.selectedContents || []).length
                            ? "Add another image"
                            : "Select content from library"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Budget & Schedule */}
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Duration & Budget</p>
                    {/* Four across only at lg (1024px). It was md, which is 768px -
                        exactly iPad portrait - and a native date input will not compress
                        below about 200px, so at 192px per cell the Start and End inputs
                        overflowed and collided (round 2). Bronze escaped it because its
                        Duration, Start and End are static text, which does compress. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Campaign Duration <span className="text-red-500">*</span></Label>
                        {newCampaign.tier === 'BRONZE' ? (
                          <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm text-muted-foreground">
                            12 Months
                          </div>
                        ) : (
                        <Select
                          value={newCampaign.duration || ""}
                          disabled={durationLocked}
                          onValueChange={(val) => {
                            // Auto-calculate end date based on duration
                            const durationDays = {
                              "1_month": 30,
                              "3_months": 90,
                              "6_months": 180,
                              "12_months": 365
                            };

                            let endDate = "";
                            if (val && newCampaign.start_at) {
                              const start = new Date(newCampaign.start_at);
                              const end = new Date(start);
                              end.setDate(end.getDate() + durationDays[val]);
                              endDate = toLocalDateString(end);
                            }

                            setNewCampaign(prev => ({
                              ...prev,
                              duration: val,
                              end_at: endDate || prev.end_at
                            }));
                          }}
                        >
                          <SelectTrigger className="h-9" data-testid="duration-select">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          {/* Round 11, SP-38. These were four hardcoded options behind
                              a Bronze test, so Admin > Tier Pricing could uncheck a
                              duration, store it, and the form would keep offering it.
                              Robin unticked 1 and 3 months for Silver, saved, and still
                              saw them here. The backend has always honoured
                              allowed_durations, so the form was offering a choice the
                              save would refuse.

                              CAMPAIGN_TIER_CONFIG is the FETCHED config, falling back
                              to the local defaults only if /settings/tier-config
                              fails. */}
                          <SelectContent>
                            {(CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.allowed_durations
                              || ALL_DURATIONS).map(d => (
                              <SelectItem key={d} value={d}>{DURATION_LABELS[d] || d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        )}
                        {durationLocked && (
                          <p className="text-[11px] text-muted-foreground">
                            Set when you paid. A different term is a new campaign.
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Start Date {newCampaign.tier !== 'BRONZE' && <span className="text-red-500">*</span>}</Label>
                        {newCampaign.tier === 'BRONZE' ? (
                          <div className="min-h-9 px-3 py-2 flex items-center rounded-md border border-border bg-muted/50 text-xs text-muted-foreground leading-snug">
                            {/* SP-9: "Set at launch" read as "when I launch this
                                campaign". Bronze starts when the Booma app does, which
                                is a different date entirely, so it names Booma. */}
                            Starts at Booma launch
                          </div>
                        ) : (
                        <Input
                          type="date"
                          value={newCampaign.start_at}
                          min={getTodayDate()}
                          disabled={startDateLocked}
                          onChange={(e) => {
                            const startDate = e.target.value;
                            // Recalculate end date if duration is set
                            let endDate = newCampaign.end_at;
                            if (newCampaign.duration && startDate) {
                              const durationDays = {
                                "1_month": 30,
                                "3_months": 90,
                                "6_months": 180,
                                "12_months": 365
                              };
                              const start = new Date(startDate);
                              const end = new Date(start);
                              end.setDate(end.getDate() + durationDays[newCampaign.duration]);
                              endDate = toLocalDateString(end);
                            }
                            setNewCampaign(prev => ({ ...prev, start_at: startDate, end_at: endDate }));
                          }}
                          className="h-9"
                          required
                          data-testid="start-date-input"
                        />
                        )}
                        {startDateLocked && (
                          <p className="text-[11px] text-muted-foreground">
                            This campaign has already started. Pause it if you need it
                            to stop.
                          </p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">End Date (Auto)</Label>
                        {newCampaign.tier === 'BRONZE' ? (
                          <div className="min-h-9 px-3 py-2 flex items-center rounded-md border border-border bg-muted/50 text-xs text-muted-foreground leading-snug">
                            {endDateNote("12_months", newCampaign.end_at)}
                          </div>
                        ) : (
                        <>
                          <Input
                            type="date"
                            value={newCampaign.end_at}
                            min={newCampaign.start_at || getTodayDate()}
                            className="h-9 bg-secondary/30"
                            disabled={!!newCampaign.duration}
                            onChange={(e) => setNewCampaign(prev => ({ ...prev, end_at: e.target.value }))}
                            data-testid="end-date-input"
                          />
                          {/* The date alone does not say what it was counted from, and
                              this field is disabled precisely because the duration
                              decides it (round 9, TP-2). */}
                          {newCampaign.duration && (
                            <p className="text-[11px] text-muted-foreground">
                              {endDateNote(newCampaign.duration, newCampaign.end_at)}
                            </p>
                          )}
                        </>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{newCampaign.tier === 'BRONZE' ? 'Campaign Budget' : 'Campaign Budget ($)'}</Label>
                        {newCampaign.tier === 'BRONZE' ? (
                          /* min-h rather than h, and shorter copy. A pinned h-9 holding a
                             ten-word sentence clipped it on iPad instead of wrapping
                             (round 2: "bronze budget container text does not fit"). */
                          <div className="min-h-9 px-3 py-2 flex items-center rounded-md border border-border bg-muted/50 text-xs text-muted-foreground leading-snug">
                            Included in your subscription
                          </div>
                        ) : (
                        // step="10", not cents (round 4, G-3). The 0.01 was inherited
                        // from a price field; a budget is not a price and nobody sets
                        // one to $500.01.
                        <Input
                          type="number"
                          min="0"
                          step="10"
                          placeholder="No limit"
                          value={newCampaign.budget_limit}
                          onChange={(e) => setNewCampaign(prev => ({ ...prev, budget_limit: e.target.value }))}
                          className="h-9"
                          data-testid="budget-input"
                        />
                        )}
                      </div>
                    </div>
                    {newCampaign.tier !== 'BRONZE' && (
                      <p className="text-xs text-muted-foreground mt-2">
                        When budget is reached, campaign defaults to Views only which are unbillable.
                      </p>
                    )}
                  </div>

                  {/* Targeting Configuration Dropdowns */}
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">Audience</p>
                    {(() => {
                      const selectedTierConfig = CAMPAIGN_TIER_CONFIG[newCampaign.tier] || CAMPAIGN_TIER_CONFIG.BRONZE;
                      const canUseBasicTargeting = selectedTierConfig.features.basic_targeting;  // Silver+
                      const canUseAdvancedTargeting = selectedTierConfig.features.advanced_targeting;  // Gold only
                      const canUseCpdc = selectedTierConfig.features.cpdc_enabled;
                      
                      return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Location</Label>
                        <Button
                          variant="outline"
                          className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? LOCKED_CONTROL : ''}`}
                          onClick={() => canUseBasicTargeting && setLocationTargetingOpen(true)}
                          disabled={!canUseBasicTargeting}
                        >
                          <span className="truncate text-sm">
                            {!canUseBasicTargeting ? "All Areas" : getLocationTargetingSummary()}
                          </span>
                          <MapPin className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                        </Button>
                        {!canUseBasicTargeting && <p className="text-xs font-medium text-gray-500">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Days</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? LOCKED_CONTROL : ''}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Days" :
                                  newCampaign.targeting.days.length === 7 ? "All Days" :
                                  newCampaign.targeting.days.length === 0 ? "Select days" :
                                  `${newCampaign.targeting.days.length} day${newCampaign.targeting.days.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="days-all"
                                  checked={newCampaign.targeting.days.length === 7}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        days: checked 
                                          ? ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
                                          : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="days-all" className="text-sm font-medium cursor-pointer">All Days</label>
                              </div>
                              {daysOfWeek.map((day) => (
                                <div key={day.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`day-${day.value}`}
                                    checked={newCampaign.targeting.days.includes(day.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => ({
                                        ...prev,
                                        targeting: {
                                          ...prev.targeting,
                                          days: checked
                                            ? [...prev.targeting.days, day.value]
                                            : prev.targeting.days.filter(d => d !== day.value)
                                        }
                                      }));
                                    }}
                                  />
                                  <label htmlFor={`day-${day.value}`} className="text-sm cursor-pointer">{day.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs font-medium text-gray-500">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Time</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? LOCKED_CONTROL : ''}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Day" :
                                  newCampaign.targeting.time_slots.length === 4 ? "All Day" :
                                  newCampaign.targeting.time_slots.length === 0 ? "Select times" :
                                  `${newCampaign.targeting.time_slots.length} slot${newCampaign.targeting.time_slots.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="time-all"
                                  checked={newCampaign.targeting.time_slots.length === 4}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        time_slots: checked 
                                          ? ["morning", "afternoon", "evening", "night"]
                                          : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="time-all" className="text-sm font-medium cursor-pointer">All Day</label>
                              </div>
                              {timeSlots.map((slot) => (
                                <div key={slot.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`time-${slot.value}`}
                                    checked={newCampaign.targeting.time_slots.includes(slot.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => ({
                                        ...prev,
                                        targeting: {
                                          ...prev.targeting,
                                          time_slots: checked
                                            ? [...prev.targeting.time_slots, slot.value]
                                            : prev.targeting.time_slots.filter(t => t !== slot.value)
                                        }
                                      }));
                                    }}
                                  />
                                  <label htmlFor={`time-${slot.value}`} className="text-sm cursor-pointer">{slot.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs font-medium text-gray-500">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Gender</Label>
                        <Select 
                          value={newCampaign.targeting.gender}
                          onValueChange={(val) => setNewCampaign(prev => ({
                            ...prev,
                            targeting: { ...prev.targeting, gender: val }
                          }))}
                          disabled={!canUseBasicTargeting}
                        >
                          <SelectTrigger className={`h-10 ${!canUseBasicTargeting ? LOCKED_CONTROL : ''}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                        {!canUseBasicTargeting && <p className="text-xs font-medium text-gray-500">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Age Range</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseBasicTargeting ? LOCKED_CONTROL : ''}`}
                              disabled={!canUseBasicTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseBasicTargeting ? "All Ages" :
                                  newCampaign.targeting.age_ranges.includes("all") ? "All Ages" :
                                  newCampaign.targeting.age_ranges.length === 0 ? "Select ages" :
                                  `${newCampaign.targeting.age_ranges.length} range${newCampaign.targeting.age_ranges.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                {/* SP-18: "when all ages is selected same as the other
                                    options all options should default to ticked.
                                    Currently they stay blank."

                                    The cause is the sentinel: this writes ["all"], and
                                    each box below reads includes(range.value), so none of
                                    them match. The sentinel is load-bearing on the
                                    backend - DEFAULT_AGE_RANGES is ["all"] and
                                    _has_selection recognises it as "no narrowing" - so it
                                    stays in the payload and only the DISPLAY changes. */}
                                <Checkbox
                                  id="age-all"
                                  checked={newCampaign.targeting.age_ranges.includes("all")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        age_ranges: checked ? ["all"] : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="age-all" className="text-sm font-medium cursor-pointer">All Ages</label>
                              </div>
                              {ageRanges.map((range) => (
                                <div key={range.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`age-${range.value}`}
                                    /* Ticked by the sentinel as well as by itself (SP-18).
                                       "All Ages" means every range, so every box should
                                       look chosen - the payload still carries ["all"]. */
                                    checked={newCampaign.targeting.age_ranges.includes("all")
                                      || newCampaign.targeting.age_ranges.includes(range.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        // Round 12, FP-12: "If all ages are selected and
                                        // user tries to click any age it will not let
                                        // them until they unclick all ages first".
                                        //
                                        // It did nothing at all, and the two halves of
                                        // the control were each right on their own.
                                        // SP-18 ticks every box under the sentinel,
                                        // because "All Ages" does mean all of them - so
                                        // a click arrives as checked=FALSE, this removed
                                        // the band from a list that only held "all",
                                        // and the empty-list fallback below put "all"
                                        // straight back. The gesture round-tripped to
                                        // where it started.
                                        //
                                        // Under the sentinel a click can only mean
                                        // "narrow to this one". Nothing else is
                                        // available to deselect FROM. Venue Type and
                                        // Interests already behave this way, which is
                                        // the comparison Joel is drawing.
                                        if (prev.targeting.age_ranges.includes("all")) {
                                          return {
                                            ...prev,
                                            targeting: { ...prev.targeting, age_ranges: [range.value] }
                                          };
                                        }
                                        let newRanges = prev.targeting.age_ranges.filter(r => r !== "all");
                                        if (checked) {
                                          newRanges = [...newRanges, range.value];
                                        } else {
                                          newRanges = newRanges.filter(r => r !== range.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            // Unticking the last band is not "target
                                            // nobody", it is "stop narrowing".
                                            age_ranges: newRanges.length === 0 ? ["all"] : newRanges
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`age-${range.value}`} className="text-sm cursor-pointer">{range.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseBasicTargeting && <p className="text-xs font-medium text-gray-500">Silver & Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Venue Type</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseAdvancedTargeting ? LOCKED_CONTROL : ''}`}
                              disabled={!canUseAdvancedTargeting}
                            >
                              <span className="truncate text-sm">
                                {/* SP-19: "for Venue type and interests change word Any
                                    to None Selected". "Any" read as a choice that had
                                    been made; nothing had been. */}
                                {!canUseAdvancedTargeting ? "None Selected" :
                                  newCampaign.targeting.venue_types.includes("any") ? "None Selected" :
                                  newCampaign.targeting.venue_types.length === 0 ? "Select venues" :
                                  `${newCampaign.targeting.venue_types.length} type${newCampaign.targeting.venue_types.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="venue-any"
                                  checked={newCampaign.targeting.venue_types.includes("any")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        venue_types: checked ? ["any"] : []
                                      }
                                    }));
                                  }}
                                />
                                {/* Round 12, FP-11. Round 7 fixed the trigger text and left this, the
                                    option INSIDE the popover, so opening the control
                                    still showed the word. Third report. */}
                                <label htmlFor="venue-any" className="text-sm font-medium cursor-pointer">{NONE_SELECTED}</label>
                              </div>
                              {venueTypes.map((venue) => (
                                <div key={venue.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`venue-${venue.value}`}
                                    checked={newCampaign.targeting.venue_types.includes(venue.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        let newTypes = prev.targeting.venue_types.filter(v => v !== "any");
                                        if (checked) {
                                          newTypes = [...newTypes, venue.value];
                                        } else {
                                          newTypes = newTypes.filter(v => v !== venue.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            venue_types: newTypes.length === 0 ? ["any"] : newTypes
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`venue-${venue.value}`} className="text-sm cursor-pointer">{venue.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseAdvancedTargeting && <p className="text-xs font-semibold text-yellow-600">Gold only</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Interests</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full h-10 justify-between text-left font-normal ${!canUseAdvancedTargeting ? LOCKED_CONTROL : ''}`}
                              disabled={!canUseAdvancedTargeting}
                            >
                              <span className="truncate text-sm">
                                {!canUseAdvancedTargeting ? "None Selected" :
                                  newCampaign.targeting.interests.includes("any") ? "None Selected" :
                                  newCampaign.targeting.interests.length === 0 ? "Select interests" :
                                  `${newCampaign.targeting.interests.length} interest${newCampaign.targeting.interests.length > 1 ? 's' : ''} selected`}
                              </span>
                              <ChevronDown className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-56 p-3" align="start">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 pb-2 border-b">
                                <Checkbox
                                  id="interest-any"
                                  checked={newCampaign.targeting.interests.includes("any")}
                                  onCheckedChange={(checked) => {
                                    setNewCampaign(prev => ({
                                      ...prev,
                                      targeting: {
                                        ...prev.targeting,
                                        interests: checked ? ["any"] : []
                                      }
                                    }));
                                  }}
                                />
                                <label htmlFor="interest-any" className="text-sm font-medium cursor-pointer">{NONE_SELECTED}</label>
                              </div>
                              {interestOptions.map((interest) => (
                                <div key={interest.value} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`interest-${interest.value}`}
                                    checked={newCampaign.targeting.interests.includes(interest.value)}
                                    onCheckedChange={(checked) => {
                                      setNewCampaign(prev => {
                                        let newInterests = prev.targeting.interests.filter(i => i !== "any");
                                        if (checked) {
                                          newInterests = [...newInterests, interest.value];
                                        } else {
                                          newInterests = newInterests.filter(i => i !== interest.value);
                                        }
                                        return {
                                          ...prev,
                                          targeting: {
                                            ...prev.targeting,
                                            interests: newInterests.length === 0 ? ["any"] : newInterests
                                          }
                                        };
                                      });
                                    }}
                                  />
                                  <label htmlFor={`interest-${interest.value}`} className="text-sm cursor-pointer">{interest.label}</label>
                                </div>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {!canUseAdvancedTargeting && <p className="text-xs font-semibold text-yellow-600">Gold only</p>}
                      </div>
                    </div>
                      );
                    })()}
                  </div>

                  {/* Offers (spec 9). The tier caps them at Bronze 0 / Silver 3 / Gold 5;
                      OfferManager owns the limit UI and hides itself entirely on Bronze.
                      This sits above the legacy Deal Settings block below, which still
                      drives ad serving until Phase 5 re-points it at offers. Both are
                      shown for one phase on purpose - removing the deal fields now would
                      break delivery for every existing campaign. */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <OfferManager
                      campaignId={editingCampaignId}
                      tier={persistedTier || newCampaign.tier}
                      pendingTier={tierChangePending ? newCampaign.tier : null}
                      campaignContent={newCampaign.selectedContents || []}
                    />
                  </div>

                  {/* Add Ons. Deal Settings used to share this row - the original
                      single-deal fields (deal_available, deal_description, offer_text,
                      cta_text), left in place while serving still read them.

                      Removed: the campaign form was rendering TWO offer systems at once,
                      and round 2 read the old one as the current one - "templated offers
                      have still not been made". It also set a trap, because
                      assert_launch_ready counts rows in db.offers: fill in Deal Settings,
                      press Launch, and you are told to add an offer.

                      The fields still exist on the model and are still written by the
                      offer-derived serving path (served_offer_copy fills offer_text and
                      deal_description per impression). They are simply no longer a thing
                      a merchant edits by hand. */}
                  {/* Always visible (2026-09-03). Both extras sat behind a Show/Hide
                      that had to be found before either could be declined - and a
                      merchant who never opened it never saw that Discovery Boost
                      existed. Each one is a switch defaulting to off, which is already
                      the "not interested" answer without a second control in front of
                      it. */}
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Add Ons</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Optional extras, charged with the campaign</p>
                  </div>

                  {/* Add Ons content. Both extras are OFF by default and turned on with a
                      switch (round 2): they were an always-visible quantity box and a
                      duration-priced toggle, which read as things you had to configure
                      rather than things you could decline. */}
                  <div className="mt-3 space-y-3">
                      {/* Sponsored Trips (#18). "Trips to sponsor" is now "Total Trips". */}
                      {(() => {
                        const editingTrips = editingCampaignId
                          ? campaigns.find((c) => c.id === editingCampaignId)
                          : null;
                        // Locked once money has been taken, exactly as Boost is. The
                        // toggle and the quantity box are then the wrong controls: they
                        // change nothing and the server refuses the edit, which is what
                        // Joel ran into. A purchase replaces them (round 9, TP-20).
                        // Round 12, FP-10. Add-ons are bought at launch and not after
                        // it, so on a paid campaign this is a receipt rather than a
                        // control. If nothing was bought there is nothing to receipt,
                        // and the panel does not render at all: a heading over an empty
                        // box reads as something that failed to load.
                        const tripsLocked = editingTrips?.payment_status === "paid";
                        const boughtTrips = editingTrips?.sponsored_trips || {};
                        const paidTrips = boughtTrips.payment_status === "paid"
                          ? (boughtTrips.quantity || 0) : 0;
                        const tripRate = globalPricing?.sponsored_trip_rate ?? 3;
                        if (tripsLocked && paidTrips === 0) return null;

                        return (
                      <div className="rounded-lg border border-border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Sponsored Trips</p>
                            {/* No max-w-prose (round 4, G-6). It caps the line at about
                                65 characters inside a panel much wider than that, so
                                the copy wrapped to four lines with empty space beside
                                it. Discovery Boost had the same constraint and the same
                                complaint; the pair is fixed together so they stay
                                balanced. */}
                            <p className="text-xs text-muted-foreground mt-1">
                              Sponsor riders&apos; trips using prepaid credit. At the end of each
                              rider&apos;s journey, your business name and logo acknowledge that you
                              helped cover their fare.
                            </p>
                          </div>
                          {!tripsLocked && (
                            <Switch
                              checked={newCampaign.sponsored_trips_enabled}
                              onCheckedChange={(v) => setNewCampaign(prev => ({
                                ...prev,
                                sponsored_trips_enabled: v,
                                // Turning it off zeroes the quantity, so a switch nobody
                                // noticed cannot leave a charge behind.
                                sponsored_trips_qty: v ? (prev.sponsored_trips_qty || 0) : 0,
                              }))}
                              data-testid="sponsored-trips-toggle"
                            />
                          )}
                        </div>

                        {tripsLocked && paidTrips > 0 && (
                          <p className="text-xs text-emerald-600 mt-2" data-testid="sponsored-trips-status">
                            {paidTrips.toLocaleString()} trip{paidTrips === 1 ? "" : "s"} purchased
                            for ${Number(boughtTrips.total_cost || 0).toFixed(2)}.
                          </p>
                        )}

                        {/* The "awaiting payment" line is gone with the control
                            that produced it (round 12, FP-8). Nothing in the product
                            can now leave a merchant owing for an add-on they did not
                            agree to buy. */}

                        {/* The purchase control was here and is GONE (round 12, FP-10).
                            Add-ons are bought at launch and not after it for MVP, which
                            also closes FP-8: a merchant who opened this to look at the
                            price and closed Stripe was left owing for trips they had
                            never agreed to buy. */}

                        {!tripsLocked && newCampaign.sponsored_trips_enabled && (() => {
                          const rate = tripRate;
                          const qty = Math.max(0, parseInt(newCampaign.sponsored_trips_qty) || 0);
                          return (
                            <div className="mt-3 grid grid-cols-3 gap-3 items-end">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Total Trips</Label>
                                <Input
                                  type="number" min="0" step="1"
                                  value={newCampaign.sponsored_trips_qty}
                                  onChange={(e) => setNewCampaign(prev => ({ ...prev, sponsored_trips_qty: Math.max(0, parseInt(e.target.value) || 0) }))}
                                  className="h-9"
                                  data-testid="sponsored-trips-qty"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Price per trip</Label>
                                <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm">${rate.toFixed(2)}</div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Total</Label>
                                <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm font-medium" data-testid="sponsored-trips-total">
                                  ${(qty * rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                        );
                      })()}

                      {/* Discovery Boost. ONE price whatever the campaign's length: it buys
                          presence in Discovery for the campaign's duration, and does not
                          buy more of anything on a longer one. There is no duration to
                          choose and none to display. Available on every tier. */}
                      {(() => {
                        const price = globalPricing?.discovery_boost_rate;
                        const editing = editingCampaignId
                          ? campaigns.find((c) => c.id === editingCampaignId)
                          : null;
                        const purchased = editing?.discovery_boost;
                        const isPaid = purchased?.payment_status === "paid";
                        // What has been PAID for, and what is merely ordered. The
                        // backend splits these for the same reason: an unpaid order
                        // must not unlock its Feature Offers (G-7).
                        const paidQty = isPaid ? (purchased?.quantity || 1) : 0;
                        const pendingQty = purchased?.pending_quantity || 0;
                        // What the merchant has ASKED for, which on an unpaid draft is
                        // what the backend allows them to build against (R2). Falls back
                        // to the form's own selector so the containers are correct the
                        // moment Boost is toggled, before any save has happened.
                        const boostOrderedQty = purchased?.quantity
                          || Math.min(BOOST_MAX_PER_CAMPAIGN,
                                      Math.max(1, Number(newCampaign.discovery_boost_qty) || 1));
                        // Once a campaign is paid its add-ons are locked against silent
                        // edits, so the switch is replaced by a path that charges.
                        const locked = editing?.payment_status === "paid";
                        // FP-10, as above: a receipt on a paid campaign, and absent
                        // when there is nothing to receipt.
                        if (locked && paidQty === 0) return null;

                        return (
                          <div className="rounded-lg border border-border p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Discovery Boost</p>
                                {/* G-6: the width, and shorter. "Ten" became "ten per
                                    Boost" when a campaign could buy two (G-7). */}
                                <p className="text-xs text-muted-foreground mt-1">
                                  Appear in Discovery mode. Each Boost buys ten Feature Offers shown
                                  throughout Discovery for your campaign&apos;s duration, beyond your
                                  tier&apos;s offer limit. One price whatever the length, and the only
                                  way a Bronze campaign appears in Discovery. Feature Offers never
                                  appear during a trip.
                                </p>
                              </div>
                              {!locked && (
                                <Switch
                                  checked={newCampaign.discovery_boost_enabled}
                                  onCheckedChange={(v) => {
                                    setNewCampaign(prev => ({ ...prev, discovery_boost_enabled: v }));
                                    // Feature Offers attach to a campaign_id, so the
                                    // draft has to exist before they can be made
                                    // (SP-11). Same reason saveDraftAndGoToCreatives
                                    // saves before navigating, and the same quiet save:
                                    // the merchant asked for Boost, not for a save.
                                    if (v && !editingCampaignId && newCampaign.name.trim()) {
                                      handleSaveCampaign(true, { quiet: true });
                                    }
                                  }}
                                  data-testid="discovery-boost-toggle"
                                />
                              )}
                            </div>

                            {isPaid && (
                              <p className="text-xs text-emerald-600 mt-2" data-testid="discovery-boost-status">
                                {paidQty > 1
                                  ? `${paidQty} Boosts purchased for $${Number(purchased.total_cost || 0).toFixed(2)}. ${paidQty * BOOST_OFFERS_PER_BOOST} Feature Offers.`
                                  : `Purchased for $${Number(purchased.total_cost || 0).toFixed(2)}. ${BOOST_OFFERS_PER_BOOST} Feature Offers.`}
                              </p>
                            )}

                            {/* The pending line is gone with the purchase control
                                (round 12, FP-9). */}

                            {/* The purchase control was here and is GONE (round 12,
                                FP-10), and with it FP-9: opening it to see the price
                                and closing Stripe left a payment pending for a Boost
                                the merchant had not agreed to. */}

                            {!locked && newCampaign.discovery_boost_enabled && (() => {
                              const boostQty = Math.min(
                                BOOST_MAX_PER_CAMPAIGN,
                                Math.max(1, Number(newCampaign.discovery_boost_qty) || 1)
                              );
                              return (
                              <div className="mt-3 space-y-3">
                                {/* Joel's three boxes, in his order (G-7): how many,
                                    how many Feature Offers, total. The same grid as
                                    Sponsored Trips above, which is what he asked for.

                                    A Select rather than a stepper: there are two lawful
                                    answers, and a control that can reach 7 invites a
                                    request the server will refuse. */}
                                <div className="grid grid-cols-3 gap-3 items-end">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Boosts</Label>
                                    <Select
                                      value={String(boostQty)}
                                      onValueChange={(v) => setNewCampaign(prev => ({ ...prev, discovery_boost_qty: Number(v) }))}
                                    >
                                      <SelectTrigger className="h-9 text-sm" data-testid="discovery-boost-qty">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Array.from({ length: BOOST_MAX_PER_CAMPAIGN }, (_, i) => i + 1).map((n) => (
                                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Feature Offers</Label>
                                    <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm" data-testid="discovery-boost-offers">
                                      {boostQty * BOOST_OFFERS_PER_BOOST}
                                    </div>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">Total</Label>
                                    <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/50 text-sm font-medium" data-testid="discovery-boost-total">
                                      {price != null ? `$${(Number(price) * boostQty).toFixed(2)}` : "-"}
                                    </div>
                                  </div>
                                </div>
                                {/* Which image the Feature Offers carry. Defaults to the
                                    business logo so Boost works without another decision;
                                    the campaign's own content is there for a merchant who
                                    wants Discovery to match the campaign. */}
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Feature Offer image</Label>
                                  <Select
                                    value={newCampaign.discovery_boost_image || "business"}
                                    onValueChange={(v) => setNewCampaign(prev => ({ ...prev, discovery_boost_image: v }))}
                                  >
                                    <SelectTrigger className="h-9 text-sm" data-testid="discovery-boost-image">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="business">Your business image</SelectItem>
                                      <SelectItem value="campaign">This campaign&apos;s content</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              );
                            })()}

                            {/* The Feature Offers themselves (G-8): "currently I select
                                discovery boost but have no idea how or where I get my 10
                                or 20 purchased offers". They are created here, with the
                                purchase that grants them, rather than in a section that
                                has to be found.

                                Shown whenever the campaign has a Boost record at all -
                                the UNPAID state is the exact moment the question gets
                                asked, so OfferManager says the offers unlock when the
                                payment clears rather than showing nothing.

                                Needs a saved campaign to attach to, so it is absent
                                during creation for the same reason the tier offers are. */}
                            {/* Round 7, SP-11. This was `editingCampaignId && purchased`,
                                and `purchased` reads the SAVED campaign's Boost record -
                                so during setup neither held and the containers were
                                absent at exactly the moment Joel looked for them. Twice.

                                Now: visible as soon as Boost is switched on. Offers need
                                a campaign_id, so an unsaved draft is TOLD to save rather
                                than shown an empty panel. */}
                            {/* Round 9, TP-8: gated on a PAID Boost, not on the mere
                                presence of a stored sub-document. `purchased` is
                                truthy for an unpaid draft order too, so switching the
                                toggle off left these controls on screen and offers
                                creatable against a Boost nobody had bought. Paid stays
                                visible whatever the toggle says, because its Feature
                                Offers are real and must not be stranded. */}
                            {(newCampaign.discovery_boost_enabled || isPaid) && (
                              <div className="mt-3 pt-3 border-t border-border">
                                {editingCampaignId ? (
                                  <OfferManager
                                    campaignId={editingCampaignId}
                                    tier={newCampaign.tier}
                                    source="DISCOVERY_BOOST"
                                    // On a DRAFT the backend counts what has been ORDERED
                                    // rather than paid (decision R2), so this must agree
                                    // or the panel would read "0 of 0" while the server
                                    // allowed ten.
                                    boostQuantity={paidQty || boostOrderedQty}
                                    campaignContent={newCampaign.selectedContents || []}
                                  />
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    Give this campaign a name and save it as a draft, and
                                    your Feature Offers can be created here.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  {/* end Add Ons */}
                </div>

                {/* Right Section - Pricing (Read-only rates from admin settings) */}
                <div className="w-56 shrink-0 border-l border-border pl-6">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-4 font-medium">Campaign Metrics</p>
                  
                  <div className="space-y-3">
                    {/* Views. Always DELIVERED, not always charged.
                        Round 7, SP-8 and decision R1: Bronze is backfill inventory and
                        its prepaid term buys that placement outright, so a per-view
                        price on Bronze was quoting a charge that should not exist. */}
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-blue-600">Views</span>
                        <Badge variant="outline" className="text-xs bg-blue-500/10 border-blue-500/30">Default</Badge>
                      </div>
                      {/* Three states, not two (round 9, TP-1). `tier` starts as "",
                          so CAMPAIGN_TIER_CONFIG[""] is undefined and this fell into
                          the Bronze branch: the panel promised "included in your
                          campaign cost" before the merchant had chosen anything, which
                          is a claim about a tier they might not pick. Nothing until
                          they choose. */}
                      {!newCampaign.tier ? null
                        : CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpm_enabled ? (
                        <>
                          <p className="text-lg font-bold mt-1">${((globalPricing?.cpm_rate || 120) / 1000).toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">per view</p>
                        </>
                      ) : (
                        // Joel: "Alternatively instead it could say covered by campaign
                        // cost but I think just showing no price is better." No price,
                        // and a line saying why - an empty panel would read as missing.
                        <p className="text-xs text-muted-foreground mt-1">
                          Included in your campaign cost
                        </p>
                      )}
                    </div>

                    {/* Clicks */}
                    <div className={`p-3 rounded-lg ${newCampaign.pricing.cpc_enabled ? 'bg-green-500/10 border-green-500/20' : 'bg-secondary/30 border-border'} border ${!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        {/* The info button is anchored to the RIGHT of the row, never
                            beside the label (round 12, FP-13, Joel's third report).
                            SP-23 moved it to items-start so the two rows' icons sat at
                            the same HEIGHT, which they now do - but "Delivered
                            Customers" wraps in this 224px panel and "Clicks" does not,
                            so an icon that follows its label still lands at a different
                            x on each row. Anchoring it right is the one placement that
                            does not move when a label's length changes. This is also
                            what the campaign DETAIL panel above has always done, so the
                            two halves of the same product now agree. */}
                        <span className={`text-xs font-medium ${newCampaign.pricing.cpc_enabled ? 'text-green-600' : 'text-muted-foreground'}`}>Clicks</span>
                        <div className="flex items-center gap-1 shrink-0">
                        <ClicksInfoButton />
                        <Switch
                          checked={newCampaign.pricing.cpc_enabled}
                          onCheckedChange={(checked) => setNewCampaign(prev => ({
                            ...prev,
                            pricing: { ...prev.pricing, cpc_enabled: checked }
                          }))}
                          disabled={!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled}
                          data-testid="cpc-toggle"
                        />
                        </div>
                      </div>
                      {!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpc_enabled && (
                        <p className="text-xs font-medium text-gray-500 mt-1">Silver or Gold only</p>
                      )}
                      {newCampaign.pricing.cpc_enabled && (
                        <>
                          <p className="text-lg font-bold mt-1">${globalPricing?.cpc_rate?.toFixed(2) || "0.35"}</p>
                          <p className="text-xs text-muted-foreground">per click</p>
                        </>
                      )}
                    </div>

                    {/* Delivered Customers */}
                    <div className={`p-3 rounded-lg ${newCampaign.pricing.cpdc_enabled ? 'bg-purple-500/10 border-purple-500/20' : 'bg-secondary/30 border-border'} border ${!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs font-medium ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>Delivered Customers</span>
                        <div className="flex items-center gap-1 shrink-0">
                        <CPDCInfoButton />
                        <Switch
                          checked={newCampaign.pricing.cpdc_enabled}
                          onCheckedChange={(checked) => setNewCampaign(prev => ({
                            ...prev,
                            pricing: { 
                              ...prev.pricing, 
                              cpdc_enabled: checked,
                              cpdc_direct_enabled: checked,
                              cpdc_proximity_enabled: checked,
                              cpdc_prompted_enabled: checked
                            }
                          }))}
                          disabled={!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled}
                          data-testid="cpdc-toggle"
                        />
                        </div>
                      </div>
                      {!CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled && (
                        <p className="text-xs font-medium text-gray-500 mt-1">Silver or Gold only</p>
                      )}
                      {/* Always show rates if the tier supports Delivered Customers, regardless of toggle state */}
                      {CAMPAIGN_TIER_CONFIG[newCampaign.tier]?.features?.cpdc_enabled && (
                        <div className={`mt-3 space-y-2 pt-2 border-t ${newCampaign.pricing.cpdc_enabled ? 'border-purple-500/20' : 'border-border'}`}>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Direct To Door</span>
                              <span className={`font-bold ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>${globalPricing?.cpdc_direct_rate?.toFixed(2) || "8.00"}</span>
                            </div>
                            {/* Nearby, not Proximity, and no Prompted row - the two
                                names a merchant is billed under are Direct and Nearby. */}
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Nearby Influenced</span>
                              <span className={`font-bold ${newCampaign.pricing.cpdc_enabled ? 'text-purple-600' : 'text-muted-foreground'}`}>${globalPricing?.cpdc_proximity_rate?.toFixed(2) || "5.00"}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Save and Launch are two different acts, so they are two buttons.
                        There used to be one, which is why a campaign could not be saved
                        without paying for it - and why offers, which need a saved
                        campaign, were unreachable during creation entirely.

                        A live campaign has already been paid for, so it only gets Save. */}
                    {persistedIsLive ? (
                      <Button
                        className="w-full mt-4"
                        onClick={() => setConfirmSaveOpen(true)}
                        disabled={saving || !newCampaign.name.trim()}
                      >
                        <Save className="mr-2 h-4 w-4" />
                        Save changes
                      </Button>
                    ) : (
                      <div className="mt-4 space-y-2">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleSaveCampaign(true)}
                          disabled={saving || !newCampaign.name.trim()}
                        >
                          <Save className="mr-2 h-4 w-4" />
                          Save draft
                        </Button>
                        <Button
                          className="w-full"
                          onClick={handleLaunch}
                          disabled={saving || !newCampaign.name.trim()}
                          data-testid="launch-campaign-btn"
                        >
                          <CreditCard className="mr-2 h-4 w-4" />
                          Launch &amp; pay
                        </Button>
                        <p className="text-[11px] text-muted-foreground text-center">
                          Saving costs nothing. You are only charged when you launch.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Add New Campaign Button. Hidden without campaigns:write (round 10):
             a dashed "create" affordance that refuses on click is worse than a list
             that simply ends. */
          canWriteCampaigns && (
          <Card 
            className="bg-card border-border border-dashed hover:border-primary/50 transition-all cursor-pointer group"
            onClick={() => { setEditingCampaignId(null); setIsCreating(true); }}
            data-testid="create-campaign-btn"
          >
            <CardContent className="flex items-center justify-center gap-4 p-8">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Plus className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-lg">New Campaign</p>
                <p className="text-sm text-muted-foreground">Create a new campaign</p>
              </div>
            </CardContent>
          </Card>
          )
        )}
      </div>

      {/* Confirm Save Dialog */}
      <Dialog open={confirmSaveOpen} onOpenChange={setConfirmSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            {/* Only reachable for a live campaign now. Saving a draft is free and
                reversible, so asking twice would be friction with nothing behind it -
                the confirmation exists for edits to something already paid for and
                running. */}
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-primary" />
              Save changes?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This campaign is live. Your changes take effect immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmSaveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveCampaign(false)} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Yes, save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Picker Dialog (#8/P2-5) */}
      <Dialog open={creativePickerOpen} onOpenChange={setCreativePickerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="h-5 w-5" />
              Select Campaign Content
              {creativeLimit > 1 && (
                <span className="text-xs font-normal text-muted-foreground">
                  {(newCampaign.selectedContents || []).length} of {creativeLimit}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {creativeLimit === 1
                ? "Pick one piece of content - it's automatically adapted to every placement."
                : `Pick up to ${creativeLimit}. Each is automatically adapted to every placement.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {contentList.filter(ct => !ct.is_draft).length === 0 ? (
              <div className="text-center py-8">
                <Image className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">No content in your library yet</p>
                <Button onClick={() => { setCreativePickerOpen(false); saveDraftAndGoToCreatives(); }}>
                  <ImagePlus className="mr-2 h-4 w-4" />
                  Create Content
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {contentList.filter(ct => !ct.is_draft).map(ct => (
                  <div
                    key={ct.id}
                    onClick={() => selectContent(ct)}
                    className={`relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all hover:border-primary ${
                      (newCampaign.selectedContents || []).some(c => c.id === ct.id) ? 'border-primary ring-2 ring-primary/30' : 'border-border'
                    }`}
                    data-testid={`content-option-${ct.id}`}
                  >
                    <div className="aspect-square bg-muted/40 flex items-center justify-center">
                      {ct.master_url ? (
                        <img src={resolveMedia(ct.master_url)} alt={ct.name} className="w-full h-full object-contain" />
                      ) : <Image className="h-8 w-8 text-muted-foreground" />}
                    </div>
                    <div className="p-2 bg-secondary/80">
                      <p className="text-sm font-medium truncate">{ct.name}</p>
                      {/* G-19: no placement count. The number was correct - old content
                          has three renditions, new content two - and a correct number
                          that a merchant can do nothing with is worse than none. */}
                      {ct.source === 'AI_GENERATED' && (
                        <p className="text-xs text-muted-foreground">AI generated</p>
                      )}
                    </div>
                    {/* A tick in the corner is reinforcement, not a signal on its own
                        (SP-20). A word across the tile is unmissable, and it doubles as
                        the way to remove it - clicking a selected tile deselects. */}
                    {(newCampaign.selectedContents || []).some(c => c.id === ct.id) && (
                      <>
                        <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                          <Check className="h-4 w-4" />
                        </div>
                        <div className="absolute inset-x-0 bottom-0 bg-primary/90 text-primary-foreground text-[11px] font-medium text-center py-1">
                          Selected, click to remove
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Round 7, SP-20. There was no way to say "I am done": the footer offered
              Cancel and Create New, so the only route out with your selection intact was
              the X, which reads as abandoning rather than confirming.

              Cancel is gone rather than renamed, because it was also untrue - selection
              applies on click and updates the campaign immediately, so it cancelled
              nothing. Done is now the primary action and says what is selected. */}
          <DialogFooter className="border-t pt-4 sm:justify-between">
            <Button variant="secondary" onClick={() => { setCreativePickerOpen(false); saveDraftAndGoToCreatives(); }}>
              <ImagePlus className="mr-2 h-4 w-4" />
              Create New
            </Button>
            <Button onClick={() => setCreativePickerOpen(false)} data-testid="content-picker-done">
              <Check className="mr-2 h-4 w-4" />
              {(newCampaign.selectedContents || []).length
                ? `Done, ${(newCampaign.selectedContents || []).length} selected`
                : "Done"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="max-w-md">
          {/* A REFUSAL, not a confirmation (round 7, SP-1).

          Round 3 shipped this as a confirm-once dialog, which asked the merchant to
          approve an outcome nobody wants: a campaign that is live, paid for, and
          delivering nothing. Joel considered the buffer variants he had floated himself
          and rejected them: "MVP can simply reject the change and ask for a higher
          amount."

          Still a dialog rather than a toast, because it carries two figures the merchant
          has to act on and a toast that vanishes is the wrong place for a number. What
          went is the way through it. */}
      <Dialog open={!!budgetStop} onOpenChange={(o) => !o && setBudgetStop(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              That budget is too low
            </DialogTitle>
            <DialogDescription className="pt-2">
              This action cannot be completed because your campaign has already spent{" "}
              <strong>${Number(budgetStop?.spent ?? 0).toFixed(2)}</strong>. Setting the
              budget to <strong>${Number(budgetStop?.new_limit ?? 0).toFixed(2)}</strong>{" "}
              would leave it nothing to spend. Please enter a higher budget.
              <br /><br />
              <span className="text-muted-foreground">
                If you want the campaign to stop, pause it instead. Pausing keeps its
                dates and you can resume whenever you like.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setBudgetStop(null)} data-testid="budget-refused-ok">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Two different actions behind one control, so the confirmation has to say
              which. An unpaid draft is DELETED - permanently, along with its offers -
              and telling that merchant "archived campaigns can be copied to create a new
              draft" would reassure them about something that is not going to happen. */}
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${deletesOutright ? "text-red-500" : "text-amber-500"}`} />
              {deletesOutright ? "Delete draft" : "Archive Campaign"}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {deletesOutright ? (
                <>
                  Delete <strong>&ldquo;{campaignToArchive?.name}&rdquo;</strong> permanently?
                  <br /><br />
                  <span className="text-muted-foreground">
                    This draft was never paid for, so there is nothing to keep. It and any
                    offers on it are removed for good - this cannot be undone.
                  </span>
                </>
              ) : (
                <>
                  Are you sure you want to archive <strong>&ldquo;{campaignToArchive?.name}&rdquo;</strong>?
                  <br /><br />
                  <span className="text-muted-foreground">
                    Archived campaigns cannot be un-archived, but they can be copied to create a new draft.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveModalOpen(false);
                setCampaignToArchive(null);
              }}
              disabled={archiving}
            >
              Cancel
            </Button>
            <Button
              variant={deletesOutright ? "destructive" : "default"}
              className={deletesOutright ? "" : "bg-amber-500 hover:bg-amber-600 text-white"}
              onClick={confirmArchive}
              disabled={archiving}
              data-testid="confirm-archive-btn"
            >
              {archiving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {deletesOutright ? "Delete permanently" : "Archive Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Targeting Modal */}
      <Dialog open={locationTargetingOpen} onOpenChange={setLocationTargetingOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Targeting
            </DialogTitle>
            <DialogDescription>
              Define where your ads will be shown. Target by your venue locations, states, postcodes, or specific suburbs.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="venues" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="venues" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Your Venues
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center gap-2">
                <Map className="h-4 w-4" />
                Custom Locations
              </TabsTrigger>
            </TabsList>
            
            {/* Your Venue Locations Tab */}
            <TabsContent value="venues" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Select which of your registered venue locations to target. Ads will show to passengers within the configured radius of these locations.
              </p>
              
              {locations.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-secondary/30">
                  <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-3">No venue locations registered</p>
                  <Link to="/locations">
                    <Button size="sm">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Location
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <span className="text-sm font-medium">{locations.length} venue{locations.length !== 1 ? 's' : ''} available</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const allIds = locations.map(l => l.id);
                        const allSelected = allIds.every(id => newCampaign.targeting.location_ids.includes(id));
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            location_ids: allSelected ? [] : allIds
                          }
                        }));
                      }}
                    >
                      {locations.every(l => newCampaign.targeting.location_ids.includes(l.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  {locations.map(loc => (
                    <div 
                      key={loc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        newCampaign.targeting.location_ids.includes(loc.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            location_ids: prev.targeting.location_ids.includes(loc.id)
                              ? prev.targeting.location_ids.filter(id => id !== loc.id)
                              : [...prev.targeting.location_ids, loc.id]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={newCampaign.targeting.location_ids.includes(loc.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{loc.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{loc.address}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {loc.radius_m}m radius
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Custom Locations Tab */}
            <TabsContent value="custom" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Target broader geographic areas by state, postcode, or suburb. Useful for reaching audiences beyond your venue locations.
              </p>
              
              {/* States Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">States</Label>
                <div className="grid grid-cols-4 gap-2">
                  {australianStates.map(state => (
                    <div
                      key={state.code}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                        newCampaign.targeting.states.includes(state.code)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setNewCampaign(prev => ({
                          ...prev,
                          targeting: {
                            ...prev.targeting,
                            states: prev.targeting.states.includes(state.code)
                              ? prev.targeting.states.filter(s => s !== state.code)
                              : [...prev.targeting.states, state.code]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={newCampaign.targeting.states.includes(state.code)}
                        className="pointer-events-none h-4 w-4"
                      />
                      <span>{state.code}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Suburb/Postcode Search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Suburbs & Postcodes</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search suburbs or postcodes..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="pl-9"
                  />
                  {searchingLocations && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {/* The locality data is GeoNames, CC BY 4.0, which is satisfied by a
                    credit with a link. Keep this if the search is rebuilt - see
                    backend/data/README.md. */}
                <p className="text-[11px] text-muted-foreground">
                  Every Australian suburb and postcode. Location data from{" "}
                  <a
                    href="https://www.geonames.org/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-foreground"
                  >
                    GeoNames
                  </a>.
                </p>

                {/* Search Results */}
                {locationSearchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {locationSearchResults.map((result, idx) => {
                      const isSuburbSelected = newCampaign.targeting.suburbs.includes(result.suburb);
                      const isPostcodeSelected = newCampaign.targeting.postcodes.includes(result.postcode);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-2 hover:bg-secondary/50 cursor-pointer border-b last:border-b-0 ${
                            isSuburbSelected || isPostcodeSelected ? 'bg-primary/5' : ''
                          }`}
                          onClick={() => {
                            // Records the pairing as well as the two values, so the chip
                            // can show "Newport 2106" and remove both together. The
                            // arrays themselves are unchanged - that is what the API
                            // takes and what the serving loops match on.
                            if (result.suburb && !isSuburbSelected) {
                              setNewCampaign(prev => ({
                                ...prev,
                                targeting: {
                                  ...prev.targeting,
                                  suburbs: [...prev.targeting.suburbs, result.suburb],
                                  postcodes: result.postcode && !prev.targeting.postcodes.includes(result.postcode)
                                    ? [...prev.targeting.postcodes, result.postcode]
                                    : prev.targeting.postcodes,
                                  locality_pairs: {
                                    ...(prev.targeting.locality_pairs || {}),
                                    ...(result.postcode ? { [result.suburb]: result.postcode } : {}),
                                  },
                                }
                              }));
                            }
                            setLocationSearch("");
                            setLocationSearchResults([]);
                          }}
                        >
                          <div>
                            <span className="font-medium text-sm">{result.suburb}</span>
                            {result.postcode && (
                              <span className="text-muted-foreground text-sm ml-2">{result.postcode}</span>
                            )}
                            {result.state && (
                              <Badge variant="outline" className="ml-2 text-xs">{result.state}</Badge>
                            )}
                          </div>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* One chip per locality (#21). This used to be two lists, guarded by
                    postcodes.filter(p => !suburbs.some(s => s.includes(p))) - a
                    substring test against a suburb NAME, which never contains its own
                    postcode, so the guard never fired and both chips always rendered. */}
                {(newCampaign.targeting.suburbs.length > 0 || newCampaign.targeting.postcodes.length > 0) && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-xs text-muted-foreground">Selected Locations</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedLocalities(newCampaign.targeting).map(({ key, label, suburb, postcode }) => (
                        <Badge
                          key={key}
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => {
                            setNewCampaign(prev => {
                              const pairs = { ...(prev.targeting.locality_pairs || {}) };
                              if (suburb) delete pairs[suburb];
                              return {
                                ...prev,
                                targeting: {
                                  ...prev.targeting,
                                  suburbs: suburb
                                    ? prev.targeting.suburbs.filter(s => s !== suburb)
                                    : prev.targeting.suburbs,
                                  postcodes: postcode
                                    ? prev.targeting.postcodes.filter(p => p !== postcode)
                                    : prev.targeting.postcodes,
                                  locality_pairs: pairs,
                                },
                              };
                            });
                          }}
                          data-testid={`locality-chip-${key}`}
                        >
                          {label}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Summary */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Current Selection</span>
              <span className="text-sm text-muted-foreground">{getLocationTargetingSummary()}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setNewCampaign(prev => ({
                    ...prev,
                    targeting: {
                      ...prev.targeting,
                      location_ids: [],
                      states: [],
                      postcodes: [],
                      suburbs: [],
                      locality_pairs: {}
                    }
                  }));
                }}
              >
                Clear All
              </Button>
              <Button onClick={() => setLocationTargetingOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CampaignPaymentModal
        open={paymentModal.open}
        onOpenChange={(open) => {
          setPaymentModal((p) => ({ ...p, open }));
          if (!open) {
            // Refresh so a draft whose payment was abandoned shows its state correctly
            fetchData();
          }
        }}
        clientSecret={paymentModal.clientSecret}
        amount={paymentModal.amount}
        breakdown={paymentModal.breakdown}
        campaignName={paymentModal.campaignName}
        token={token}
        onSuccess={() => {
          setPaymentModal({ open: false, clientSecret: null, amount: 0, breakdown: null, campaignName: "" });
          // The campaign was launched from an open form in the create flow; close it so
          // the merchant lands back on their campaign list rather than on a stale form
          // for a campaign that is now live.
          setIsCreating(false);
          setEditingCampaignId(null);
          resetNewCampaign();
          settleAfterPayment();
        }}
      />
    </div>
  );
}
